import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Stack,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    useTheme,
    alpha,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Tooltip,
    Popper,
    ClickAwayListener,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    InputAdornment,
    TablePagination,
    Snackbar
} from '@mui/material';
import {
    Speed as SpeedIcon,
    Verified as QualityControlIcon,
    Add as AddIcon,
    Inventory as InventoryIcon,
    Delete as DeleteIcon,
    Edit as EditTestIcon,
    Visibility as ViewIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Cancel as CancelIcon,
    Assignment as AssignmentIcon,
    Sanitizer as BottleIcon,
    FiberManualRecord as CapIcon,
    CameraRoll as LabelIcon,
    Inventory as SupplyIcon,
    Biotech as RawMaterialIcon,
    FormatBold as BoldIcon,
    FormatItalic as ItalicIcon,
    FormatListBulleted as BulletIcon,
    FormatListNumbered as NumberedIcon,
    Code as CodeIcon,
    Link as LinkIcon,
    TableChart as TableIcon,
    HorizontalRule as HRuleIcon,
    FormatQuote as QuoteIcon,
    Title as HeaderIcon,
    ViewColumn as ColumnsIcon,
    Info as InfoIcon,
    Error as DangerIcon,
    List as TOCIcon,
    Visibility as PreviewIcon,
    VisibilityOff as EditIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Build as EquipmentIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';

// Markdown Toolbar Component (same as original)
const MarkdownToolbar = ({ onInsert }) => {
    const theme = useTheme();

    const tools = [
        { icon: <BoldIcon />, action: () => onInsert('**', '**'), tooltip: 'Bold (Ctrl+B)', shortcut: 'Ctrl+B' },
        { icon: <ItalicIcon />, action: () => onInsert('*', '*'), tooltip: 'Italic (Ctrl+I)', shortcut: 'Ctrl+I' },
        { icon: <BulletIcon />, action: () => onInsert('\n- ', ''), tooltip: 'Bullet List (Ctrl+Shift+8)', shortcut: 'Ctrl+Shift+8' },
        { icon: <NumberedIcon />, action: () => onInsert('\n1. ', ''), tooltip: 'Numbered List (Ctrl+Shift+7)', shortcut: 'Ctrl+Shift+7' },
        { icon: <CodeIcon />, action: () => onInsert('`', '`'), tooltip: 'Code (Ctrl+`)', shortcut: 'Ctrl+`' },
        { icon: <LinkIcon />, action: () => onInsert('[', '](url)'), tooltip: 'Link (Ctrl+K)', shortcut: 'Ctrl+K' },
        { icon: <TableIcon />, action: () => onInsert('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', ''), tooltip: 'Table (Ctrl+T)', shortcut: 'Ctrl+T' },
        { icon: <HRuleIcon />, action: () => onInsert('\n---\n', ''), tooltip: 'Horizontal Rule (Ctrl+H)', shortcut: 'Ctrl+H' }
    ];

    return (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            p: 2, 
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: alpha(theme.palette.grey[50], 0.8)
        }}>
            {tools.map((tool, index) => (
                <Tooltip
                    key={index}
                    title={tool.tooltip}
                    placement="top"
                    arrow
                >
                    <IconButton
                        size="small"
                        onClick={tool.action}
                        sx={{
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                            borderRadius: 2,
                            backgroundColor: 'white',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderColor: theme.palette.primary.main
                            },
                            '&:active': {
                                transform: 'scale(0.95)'
                            },
                            transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease'
                        }}
                    >
                        {tool.icon}
                    </IconButton>
                </Tooltip>
            ))}
        </Box>
    );
};

// Equipment Display Component
const EquipmentDisplay = ({ testId }) => {
    const theme = useTheme();
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadEquipment = async () => {
            if (!testId) {
                setLoading(false);
                return;
            }
            
            try {
                const { data, error } = await supabase.rpc('get_inventory_qc_test_equipment', {
                    p_qc_test_id: testId
                });
                
                if (error) throw error;
                setEquipment(data || []);
            } catch (error) {
                console.error('Error loading equipment:', error);
                setEquipment([]);
            } finally {
                setLoading(false);
            }
        };

        loadEquipment();
    }, [testId]);

    if (loading) {
        return <Typography>Loading equipment...</Typography>;
    }

    if (equipment.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <EquipmentIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
                <Typography variant="subtitle1" color="text.secondary">
                    No Equipment Recorded
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    No equipment was recorded for this test
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {equipment.map((eq, index) => (
                <Grid item xs={12} key={eq.id}>
                    <Card sx={{ 
                        p: 2.5, 
                        backgroundColor: alpha(theme.palette.grey[50], 0.8),
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <EquipmentIcon sx={{ mr: 1.5, color: theme.palette.primary.main, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                {eq.equipment_name}
                            </Typography>
                            <Chip 
                                label={eq.equipment_number} 
                                size="small" 
                                sx={{ 
                                    ml: 2,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500
                                }} 
                            />
                        </Box>
                        <Typography variant="body1" sx={{ 
                            lineHeight: 1.6,
                            color: theme.palette.text.primary,
                            whiteSpace: 'pre-wrap'
                        }}>
                            {eq.usage_description}
                        </Typography>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

const SupplyQCTests = () => {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    
    // Permission checks
    const canEdit = hasPermission('inventory.supplies.qc_tests.edit', 'write');
    const canDelete = hasPermission('inventory.supplies.qc_tests.delete', 'delete');
    const canApprove = hasPermission('inventory.supplies.qc_tests.approve', 'write');
    const [qualityChecks, setQualityChecks] = useState([]);
    const [newQcTest, setNewQcTest] = useState({
        category: 'bottles',
        test_name: 'Visual Inspection - Clarity/Color',
        test_type: 'bottle_visual',
        expected_value: '',
        actual_value: '',
        unit_of_measure: '',
        pass_fail_status: 'PENDING',
        tested_at: dayjs(),
        tested_by: '',
        notes: '',
        batch_number: '',
        lot_number: '',
        received_date: dayjs(),
        sample_size: '',
        sample_id: '',
        deviation_notes: '',
        material_name: '',
        material_supplier: '',
        supplier_id: null
    });
    
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedTest, setSelectedTest] = useState(null);
    const [testToDelete, setTestToDelete] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editingTestId, setEditingTestId] = useState(null);
    const [errors, setErrors] = useState({});
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    // Review state
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    const [testUnderReview, setTestUnderReview] = useState(null);
    const [reviewerName, setReviewerName] = useState('');
    const [reviewerRole, setReviewerRole] = useState('qa_manager');
    const [reviewComments, setReviewComments] = useState('');
    const [reviewing, setReviewing] = useState(false);
    const [signaturePreview, setSignaturePreview] = useState('');
    
    // Unapprove state
    const [showUnapproveDialog, setShowUnapproveDialog] = useState(false);
    const [testToUnapprove, setTestToUnapprove] = useState(null);
    const [unapproveReason, setUnapproveReason] = useState('');
    const [unapproveSignature, setUnapproveSignature] = useState('');
    
    // Lot/Batch selection dialog state
    const [showLotBatchDialog, setShowLotBatchDialog] = useState(false);
    const [lotBatchSearchTerm, setLotBatchSearchTerm] = useState('');
    const [availableLotBatches, setAvailableLotBatches] = useState([]);
    const [filteredLotBatches, setFilteredLotBatches] = useState([]);
    const [selectedLotBatch, setSelectedLotBatch] = useState(null);
    const [loadingLotBatches, setLoadingLotBatches] = useState(false);
    const [unapproveSignaturePreview, setUnapproveSignaturePreview] = useState('');
    const [unapproving, setUnapproving] = useState(false);
    
    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterQAStatus, setFilterQAStatus] = useState('all');
    const [filterTestStatus, setFilterTestStatus] = useState('all');
    const [filteredQualityChecks, setFilteredQualityChecks] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Equipment state
    const [equipmentList, setEquipmentList] = useState([]);
    const [showAddEquipment, setShowAddEquipment] = useState(false);
    
    // Equipment selection dialog state
    const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
    const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
    const [availableEquipment, setAvailableEquipment] = useState([]);
    const [filteredEquipment, setFilteredEquipment] = useState([]);
    const [selectedEquipmentFromList, setSelectedEquipmentFromList] = useState(null);
    const [loadingEquipment, setLoadingEquipment] = useState(false);
    const [currentEquipmentIndex, setCurrentEquipmentIndex] = useState(null);
    
    // Markdown editor states
    const [expectedValueRef, setExpectedValueRef] = useState(null);
    const [actualValueRef, setActualValueRef] = useState(null);
    const [expectedPreviewMode, setExpectedPreviewMode] = useState(false);
    const [actualPreviewMode, setActualPreviewMode] = useState(false);
    const [commandMenuOpen, setCommandMenuOpen] = useState(false);
    const [commandMenuAnchor, setCommandMenuAnchor] = useState(null);
    const [filteredCommands, setFilteredCommands] = useState([]);
    const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [activeField, setActiveField] = useState('');
    
    // Template states
    const [templates, setTemplates] = useState([]);
    const [showTemplateDialog, setShowTemplateDialog] = useState(false);
    const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
    const [showDeleteTemplateDialog, setShowDeleteTemplateDialog] = useState(false);
    const [templateType, setTemplateType] = useState('specification'); // 'specification' or 'result'
    const [newTemplateName, setNewTemplateName] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Command sections for slash commands (same as original)
    const commandSections = [
        {
            title: 'FORMAT',
            commands: [
                { name: '/heading1', alias: '/h1', icon: <HeaderIcon />, description: 'Big section heading', action: () => insertMarkdown('\n# ', '') },
                { name: '/heading2', alias: '/h2', icon: <HeaderIcon />, description: 'Medium section heading', action: () => insertMarkdown('\n## ', '') },
                { name: '/heading3', alias: '/h3', icon: <HeaderIcon />, description: 'Small section heading', action: () => insertMarkdown('\n### ', '') },
                { name: '/text', alias: '/p', icon: <AssignmentIcon />, description: 'Paragraph block', action: () => insertMarkdown('\n', '') },
                { name: '/quote', alias: '/q', icon: <QuoteIcon />, description: 'Add a blockquote section', action: () => insertMarkdown('\n> ', '') },
                { name: '/code', alias: '/c', icon: <CodeIcon />, description: 'Add a code section', action: () => insertMarkdown('\n```\n', '\n```\n') },
                { name: '/bold', alias: '/b', icon: <BoldIcon />, description: 'Bold text', action: () => insertMarkdown('**', '**') },
                { name: '/italic', alias: '/i', icon: <ItalicIcon />, description: 'Italic text', action: () => insertMarkdown('*', '*') }
            ]
        },
        {
            title: 'STRUCTURE',
            commands: [
                { name: '/separator', alias: '/hr', icon: <HRuleIcon />, description: 'Insert a horizontal rule separator', action: () => insertMarkdown('\n---\n', '') },
                { name: '/bullet', alias: '/ul', icon: <BulletIcon />, description: 'Create a simple bulleted list', action: () => insertMarkdown('\n- ', '') },
                { name: '/number', alias: '/ol', icon: <NumberedIcon />, description: 'Create a list with numbering', action: () => insertMarkdown('\n1. ', '') },
                { name: '/2columns', alias: '/2col', icon: <ColumnsIcon />, description: 'Convert into 2 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 |\n|----------|----------|\n| ', ' | |\n') },
                { name: '/3columns', alias: '/3col', icon: <ColumnsIcon />, description: 'Convert into 3 columns', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', ' | | |\n') },
                { name: '/table', alias: '/t', icon: <TableIcon />, description: 'Insert a table', action: () => insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '') },
                { name: '/toc', alias: '/contents', icon: <TOCIcon />, description: 'Table of Contents', action: () => generateTableOfContents() }
            ]
        },
        {
            title: 'BANNER',
            commands: [
                { name: '/info', alias: '/i', icon: <InfoIcon />, description: 'Insert an info banner', action: () => insertMarkdown('\n> ℹ️ **Info:** ', '\n') },
                { name: '/success', alias: '/s', icon: <CheckCircleIcon />, description: 'Insert a success banner', action: () => insertMarkdown('\n> ✅ **Success:** ', '\n') },
                { name: '/warning', alias: '/w', icon: <WarningIcon />, description: 'Insert a warning banner', action: () => insertMarkdown('\n> ⚠️ **Warning:** ', '\n') },
                { name: '/danger', alias: '/d', icon: <DangerIcon />, description: 'Insert a danger banner', action: () => insertMarkdown('\n> ❌ **Danger:** ', '\n') }
            ]
        },
        {
            title: 'NAVIGATION',
            commands: [
                { name: '/link', alias: '/l', icon: <LinkIcon />, description: 'Add a link', action: () => insertMarkdown('[', '](url)') }
            ]
        }
    ];

    // Flatten commands for filtering
    const commands = commandSections.flatMap(section => section.commands);

    // Quality Control Categories - adapted for inventory supplies only
    const qcCategories = {
        bottles: {
            name: 'Bottles',
            icon: BottleIcon,
            testTypes: [
                { value: 'bottle_visual', label: 'Visual Inspection - Clarity/Color' },
                { value: 'bottle_dimensional', label: 'Dimensional Check - Height/Diameter' },
                { value: 'bottle_weight', label: 'Weight Verification' },
                { value: 'bottle_wall_thickness', label: 'Wall Thickness' },
                { value: 'bottle_neck_finish', label: 'Neck Finish/Threading' },
                { value: 'bottle_capacity', label: 'Capacity/Volume Check' },
                { value: 'bottle_stress_crack', label: 'Stress Crack Resistance' },
                { value: 'bottle_leak_test', label: 'Leak Test' },
                { value: 'bottle_drop_test', label: 'Drop Test' },
                { value: 'bottle_material', label: 'Material Composition' }
            ]
        },
        caps: {
            name: 'Caps/Closures',
            icon: CapIcon,
            testTypes: [
                { value: 'cap_visual', label: 'Visual Inspection - Defects' },
                { value: 'cap_dimensional', label: 'Dimensional Check - Diameter/Height' },
                { value: 'cap_weight', label: 'Weight Verification' },
                { value: 'cap_thread_engagement', label: 'Thread Engagement' },
                { value: 'cap_torque_application', label: 'Application Torque' },
                { value: 'cap_torque_removal', label: 'Removal Torque' },
                { value: 'cap_seal_integrity', label: 'Seal Integrity' },
                { value: 'cap_liner_inspection', label: 'Liner Inspection' },
                { value: 'cap_tamper_evidence', label: 'Tamper Evidence' },
                { value: 'cap_compression_test', label: 'Compression Test' }
            ]
        },
        labels: {
            name: 'Labels',
            icon: LabelIcon,
            testTypes: [
                { value: 'label_visual', label: 'Visual Inspection - Print Quality' },
                { value: 'label_dimensional', label: 'Dimensional Check - Size/Position' },
                { value: 'label_adhesion', label: 'Adhesion Test' },
                { value: 'label_print_quality', label: 'Print Quality/Readability' },
                { value: 'label_color_match', label: 'Color Matching' },
                { value: 'label_barcode_scan', label: 'Barcode Scanability' },
                { value: 'label_text_accuracy', label: 'Text Accuracy/Content' },
                { value: 'label_placement', label: 'Placement/Alignment' },
                { value: 'label_bubble_wrinkle', label: 'Bubble/Wrinkle Check' },
                { value: 'label_durability', label: 'Durability/Scratch Test' }
            ]
        },
        supplies: {
            name: 'Supplies',
            icon: SupplyIcon,
            testTypes: [
                { value: 'supplies_visual', label: 'Visual Inspection' },
                { value: 'supplies_dimensional', label: 'Dimensional Verification' },
                { value: 'supplies_weight', label: 'Weight Check' },
                { value: 'supplies_strength', label: 'Strength/Durability Test' },
                { value: 'supplies_adhesive', label: 'Adhesive Performance (Tape)' },
                { value: 'supplies_box_integrity', label: 'Box/Container Integrity' },
                { value: 'supplies_seal_quality', label: 'Seal Quality' },
                { value: 'supplies_material', label: 'Material Specification' },
                { value: 'supplies_quantity', label: 'Quantity Verification' },
                { value: 'supplies_custom', label: 'Custom Test' }
            ]
        }
    };

    const [selectedCategory, setSelectedCategory] = useState('bottles');
    const [customSupplyType, setCustomSupplyType] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [isCustomTest, setIsCustomTest] = useState(false);
    const [customTestName, setCustomTestName] = useState('');

    // Load QC tests from backend
    useEffect(() => {
        loadQcTests();
    }, []);

    // Filter QC tests
    useEffect(() => {
        filterQcTests();
    }, [qualityChecks, searchTerm, filterCategory, filterQAStatus, filterTestStatus]);

    const filterQcTests = () => {
        let filtered = qualityChecks;

        if (searchTerm) {
            filtered = filtered.filter(test =>
                test.test_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                test.tested_by.toLowerCase().includes(searchTerm.toLowerCase()) ||
                test.batch_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                test.lot_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                test.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                test.material_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (filterCategory !== 'all') {
            filtered = filtered.filter(test => test.category === filterCategory);
        }

        if (filterQAStatus !== 'all') {
            filtered = filtered.filter(test => {
                const qaStatus = test.qa_review_status || 'PENDING';
                return qaStatus === filterQAStatus;
            });
        }

        if (filterTestStatus !== 'all') {
            filtered = filtered.filter(test => test.pass_fail_status === filterTestStatus);
        }

        setFilteredQualityChecks(filtered);
        setPage(0); // Reset to first page when filtering
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const loadQcTests = async () => {
        setLoading(true);
        try {
            // Get current user's company
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            // Get QC tests with material names
            const { data: qcTests, error: qcError } = await supabase.rpc('get_inventory_qc_tests', {
                p_company_id: companyData.id
            });

            if (qcError) throw qcError;

            // Now fetch the material names for each test based on category
            const testsWithMaterialNames = await Promise.all(qcTests.map(async (test) => {
                let materialName = 'Unknown Material';
                
                try {
                    switch (test.category) {
                        case 'bottles':
                            if (test.batch_number || test.lot_number) {
                                const { data: bottleData } = await supabase
                                    .from('bottle_inventory_items')
                                    .select(`
                                        bottles!inner (
                                            name
                                        )
                                    `)
                                    .eq('company_id', companyData.id)
                                    .or(`batch_number.eq.${test.batch_number || ''},lot_number.eq.${test.lot_number || ''}`)
                                    .limit(1)
                                    .single();
                                
                                if (bottleData?.bottles?.name) {
                                    materialName = bottleData.bottles.name;
                                }
                            }
                            break;
                            
                        case 'caps':
                            if (test.batch_number || test.lot_number) {
                                const { data: capData } = await supabase
                                    .from('caps_inventory_items')
                                    .select(`
                                        caps!inner (
                                            name
                                        )
                                    `)
                                    .eq('company_id', companyData.id)
                                    .or(`batch_number.eq.${test.batch_number || ''},lot_number.eq.${test.lot_number || ''}`)
                                    .limit(1)
                                    .single();
                                
                                if (capData?.caps?.name) {
                                    materialName = capData.caps.name;
                                }
                            }
                            break;
                            
                        case 'labels':
                            if (test.batch_number || test.lot_number) {
                                const { data: labelData } = await supabase
                                    .from('label_inventory_items')
                                    .select(`
                                        labels!inner (
                                            name
                                        )
                                    `)
                                    .eq('company_id', companyData.id)
                                    .or(`batch_number.eq.${test.batch_number || ''},lot_number.eq.${test.lot_number || ''}`)
                                    .limit(1)
                                    .single();
                                
                                if (labelData?.labels?.name) {
                                    materialName = labelData.labels.name;
                                }
                            }
                            break;
                            
                        case 'supplies':
                            if (test.batch_number || test.lot_number) {
                                const { data: supplyData } = await supabase
                                    .from('supply_inventory_items')
                                    .select(`
                                        supplies!inner (
                                            name
                                        )
                                    `)
                                    .eq('company_id', companyData.id)
                                    .or(`batch_number.eq.${test.batch_number || ''},lot_number.eq.${test.lot_number || ''}`)
                                    .limit(1)
                                    .single();
                                
                                if (supplyData?.supplies?.name) {
                                    materialName = supplyData.supplies.name;
                                }
                            }
                            break;
                    }
                } catch (materialError) {
                    console.warn(`Could not fetch material name for test ${test.id}:`, materialError);
                }
                
                return {
                    ...test,
                    material_name: materialName,
                    tested_at: dayjs(test.tested_at),
                    received_date: test.received_date ? dayjs(test.received_date) : null
                };
            }));
            
            setQualityChecks(testsWithMaterialNames);
        } catch (error) {
            console.error('Error loading inventory QC tests:', error);
        } finally {
            setLoading(false);
        }
    };

    // Rest of the functions would be similar to the original QualityCheck component
    // but adapted for inventory QC tests...

    const saveQcTest = async (testData) => {
        try {
            // Get current user's company
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const { data, error } = await supabase.rpc('save_inventory_qc_test', {
                p_company_id: companyData.id,
                p_category: testData.category,
                p_test_name: testData.test_name,
                p_test_type: testData.test_type,
                p_is_custom_test: testData.is_custom_test || false,
                p_custom_supply_type: testData.custom_supply_type || null,
                p_material_name: testData.material_name || null,
                p_material_supplier: testData.material_supplier || null,
                p_supplier_id: testData.supplier_id || null,
                p_expected_value: testData.expected_value || '',
                p_actual_value: testData.actual_value || '',
                p_unit_of_measure: testData.unit_of_measure || null,
                p_pass_fail_status: testData.pass_fail_status || 'PENDING',
                p_tested_at: testData.tested_at,
                p_tested_by: testData.tested_by || '',
                p_notes: testData.notes || null,
                p_batch_number: testData.batch_number || null,
                p_lot_number: testData.lot_number || null,
                p_received_date: testData.received_date || null,
                p_sample_size: testData.sample_size || null,
                p_sample_id: testData.sample_id || null,
                p_specification_reference: null,
                p_deviation_notes: testData.deviation_notes || null,
                p_test_id: testData.id || null
            });

            if (error) throw error;
            
            return data;
        } catch (error) {
            console.error('Error saving inventory QC test:', error);
            throw error;
        }
    };

    // Markdown helper functions
    const insertMarkdown = (before, after) => {
        const currentRef = activeField === 'expected' ? expectedValueRef : actualValueRef;
        const currentValue = activeField === 'expected' ? newQcTest.expected_value : newQcTest.actual_value;
        
        if (currentRef) {
            const start = currentRef.selectionStart || 0;
            const end = currentRef.selectionEnd || 0;
            const selectedText = currentValue.substring(start, end);
            const newText = currentValue.substring(0, start) + 
                           before + selectedText + after + 
                           currentValue.substring(end);
            
            if (activeField === 'expected') {
                setNewQcTest(prev => ({ ...prev, expected_value: newText }));
            } else {
                setNewQcTest(prev => ({ ...prev, actual_value: newText }));
            }
            
            setTimeout(() => {
                currentRef.focus();
                currentRef.setSelectionRange(
                    start + before.length,
                    start + before.length + selectedText.length
                );
            }, 0);
        } else {
            const newText = currentValue + before + after;
            if (activeField === 'expected') {
                setNewQcTest(prev => ({ ...prev, expected_value: newText }));
            } else {
                setNewQcTest(prev => ({ ...prev, actual_value: newText }));
            }
        }
    };

    const generateTableOfContents = () => {
        const currentValue = activeField === 'expected' ? newQcTest.expected_value : newQcTest.actual_value;
        const currentRef = activeField === 'expected' ? expectedValueRef : actualValueRef;
        
        const lines = currentValue.split('\n');
        const headers = [];
        
        // Find all headers in the document
        lines.forEach((line, index) => {
            const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
            if (headerMatch) {
                const level = headerMatch[1].length;
                const title = headerMatch[2].trim();
                const anchor = title.toLowerCase()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '');
                
                headers.push({ level, title, anchor });
            }
        });
        
        // Generate TOC markdown
        let tocMarkdown;
        
        if (headers.length === 0) {
            tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
        } else {
            tocMarkdown = '## Table of Contents\n\n';
            headers.forEach(header => {
                const indent = '  '.repeat(Math.max(0, header.level - 1));
                tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
            });
        }
        
        // Get cursor position and insert the TOC
        const cursorPos = currentRef?.selectionStart || 0;
        const beforeCursor = currentValue.substring(0, cursorPos);
        const afterCursor = currentValue.substring(cursorPos);
        
        const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
        
        if (activeField === 'expected') {
            setNewQcTest(prev => ({ ...prev, expected_value: newText }));
        } else {
            setNewQcTest(prev => ({ ...prev, actual_value: newText }));
        }
        
        // Position cursor after the TOC
        setTimeout(() => {
            const newCursorPos = cursorPos + tocMarkdown.length + 2;
            currentRef?.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleInputChange = (field, value) => {
        setNewQcTest(prev => ({ ...prev, [field]: value }));
        
        // Handle slash command menu
        if (field === 'expected_value' || field === 'actual_value') {
            const currentRef = field === 'expected_value' ? expectedValueRef : actualValueRef;
            const cursorPos = currentRef?.selectionStart || value.length;
            const beforeCursor = value.substring(0, cursorPos);
            const lines = beforeCursor.split('\n');
            const currentLine = lines[lines.length - 1];
            
            // Show menu when line starts with / (even just "/")
            if (currentLine.startsWith('/')) {
                const searchTerm = currentLine.substring(1).toLowerCase(); // Remove the /
                let filtered;
                
                if (searchTerm === '') {
                    // Show all commands when just "/" is typed
                    filtered = commands;
                } else {
                    // Filter commands based on search term - more flexible matching
                    filtered = commands.filter(cmd => {
                        const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
                        const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
                        const descMatch = cmd.description.toLowerCase().includes(searchTerm);
                        
                        // Special keyword matching
                        const keywordMatches = {
                            'head': cmd.name.includes('heading'),
                            'header': cmd.name.includes('heading'),
                            'h': cmd.name.includes('heading'),
                            'list': cmd.name.includes('bullet') || cmd.name.includes('number'),
                            'table': cmd.name.includes('table') || cmd.name.includes('column'),
                            'banner': cmd.name.includes('info') || cmd.name.includes('success') || cmd.name.includes('warning') || cmd.name.includes('danger'),
                            'format': cmd.name.includes('bold') || cmd.name.includes('italic') || cmd.name.includes('code'),
                            'text': cmd.name.includes('text') || cmd.name.includes('quote') || cmd.name.includes('bold') || cmd.name.includes('italic')
                        };
                        
                        const keywordMatch = keywordMatches[searchTerm] || false;
                        
                        return nameMatch || aliasMatch || descMatch || keywordMatch;
                    });
                }
                
                if (filtered.length > 0) {
                    setFilteredCommands(filtered);
                    setSelectedCommandIndex(0);
                    setCommandMenuOpen(true);
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    
                    // Position menu at cursor line
                    setTimeout(() => {
                        if (currentRef) {
                            // Get the textarea element
                            const textarea = currentRef;
                            const rect = textarea.getBoundingClientRect();
                            
                            // Create a canvas to measure text width accurately
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            const computedStyle = window.getComputedStyle(textarea);
                            ctx.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;
                            
                            // Get text before cursor and split into lines
                            const textBeforeCursor = value.substring(0, cursorPos);
                            const lines = textBeforeCursor.split('\n');
                            const currentLineText = lines[lines.length - 1];
                            const lineNumber = lines.length - 1;
                            
                            // Calculate line height
                            const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
                            const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
                            const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                            
                            // Calculate cursor position
                            const lineY = paddingTop + (lineNumber * lineHeight) - textarea.scrollTop;
                            const lineX = paddingLeft + ctx.measureText(currentLineText).width;
                            
                            // Position menu
                            setCommandMenuAnchor({
                                getBoundingClientRect: () => ({
                                    left: rect.left + lineX + 5,
                                    top: rect.top + lineY + lineHeight,
                                    right: rect.left + lineX + 5,
                                    bottom: rect.top + lineY + lineHeight,
                                    width: 0,
                                    height: 0
                                })
                            });
                        }
                    }, 10);
                } else {
                    setCommandMenuOpen(false);
                }
            } else {
                setCommandMenuOpen(false);
            }
        }
    };

    const executeCommand = (command) => {
        const currentRef = activeField === 'expected' ? expectedValueRef : actualValueRef;
        const currentValue = activeField === 'expected' ? newQcTest.expected_value : newQcTest.actual_value;
        const cursorPos = currentRef?.selectionStart || currentValue.length;
        const beforeCursor = currentValue.substring(0, cursorPos);
        const afterCursor = currentValue.substring(cursorPos);
        
        // Find the start of the current line
        const lineStart = beforeCursor.lastIndexOf('\n') + 1;
        const currentLine = beforeCursor.substring(lineStart);
        
        // Remove the entire slash command (including the /) from current line
        const slashIndex = currentLine.lastIndexOf('/');
        if (slashIndex !== -1) {
            const beforeSlash = beforeCursor.substring(0, lineStart + slashIndex);
            const cleanedText = beforeSlash + afterCursor;
            const newCursorPos = lineStart + slashIndex;
            
            setCommandMenuOpen(false);
            
            // Execute command directly with cleaned text
            setTimeout(() => {
                // Manually execute the markdown insertion with cleaned text
                const insertAt = newCursorPos;
                let finalText;
                
                if (command.name === '/bold') {
                    finalText = cleanedText.substring(0, insertAt) + '****' + cleanedText.substring(insertAt);
                    if (activeField === 'expected') {
                        setNewQcTest(prev => ({ ...prev, expected_value: finalText }));
                    } else {
                        setNewQcTest(prev => ({ ...prev, actual_value: finalText }));
                    }
                    setTimeout(() => {
                        currentRef?.setSelectionRange(insertAt + 2, insertAt + 2);
                    }, 0);
                } else if (command.name === '/italic') {
                    finalText = cleanedText.substring(0, insertAt) + '**' + cleanedText.substring(insertAt);
                    if (activeField === 'expected') {
                        setNewQcTest(prev => ({ ...prev, expected_value: finalText }));
                    } else {
                        setNewQcTest(prev => ({ ...prev, actual_value: finalText }));
                    }
                    setTimeout(() => {
                        currentRef?.setSelectionRange(insertAt + 1, insertAt + 1);
                    }, 0);
                } else {
                    // For other commands, extract the before/after parts from the action
                    const commandActions = {
                        '/heading1': { before: '\n# ', after: '' },
                        '/heading2': { before: '\n## ', after: '' },
                        '/heading3': { before: '\n### ', after: '' },
                        '/text': { before: '\n', after: '' },
                        '/quote': { before: '\n> ', after: '' },
                        '/code': { before: '\n```\n', after: '\n```\n' },
                        '/separator': { before: '\n---\n', after: '' },
                        '/bullet': { before: '\n- ', after: '' },
                        '/number': { before: '\n1. ', after: '' },
                        '/2columns': { before: '\n| Column 1 | Column 2 |\n|----------|----------|\n| ', after: ' | |\n' },
                        '/3columns': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| ', after: ' | | |\n' },
                        '/table': { before: '\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', after: '' },
                        '/info': { before: '\n> ℹ️ **Info:** ', after: '\n' },
                        '/success': { before: '\n> ✅ **Success:** ', after: '\n' },
                        '/warning': { before: '\n> ⚠️ **Warning:** ', after: '\n' },
                        '/danger': { before: '\n> ❌ **Danger:** ', after: '\n' },
                        '/link': { before: '[', after: '](url)' },
                        '/toc': null // Special handling for TOC
                    };
                    
                    const action = commandActions[command.name];
                    if (action === null && command.name === '/toc') {
                        // Special handling for Table of Contents - use the cleaned text
                        setTimeout(() => {
                            // Set the cleaned text first (without /toc)
                            if (activeField === 'expected') {
                                setNewQcTest(prev => ({ ...prev, expected_value: cleanedText }));
                            } else {
                                setNewQcTest(prev => ({ ...prev, actual_value: cleanedText }));
                            }
                            
                            // Then generate TOC with the cleaned text
                            setTimeout(() => {
                                if (currentRef) {
                                    currentRef.focus();
                                    currentRef.setSelectionRange(insertAt, insertAt);
                                }
                                
                                // Generate TOC using the cleaned text
                                const currentText = cleanedText;
                                const lines = currentText.split('\n');
                                const headers = [];
                                
                                // Find all headers in the document
                                lines.forEach((line, index) => {
                                    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
                                    if (headerMatch) {
                                        const level = headerMatch[1].length;
                                        const title = headerMatch[2].trim();
                                        const anchor = title.toLowerCase()
                                            .replace(/[^\w\s-]/g, '')
                                            .replace(/\s+/g, '-')
                                            .replace(/-+/g, '-')
                                            .replace(/^-|-$/g, '');
                                        
                                        headers.push({ level, title, anchor });
                                    }
                                });
                                
                                // Generate TOC markdown
                                let tocMarkdown;
                                
                                if (headers.length === 0) {
                                    tocMarkdown = `## Table of Contents

> Add headings to fill the Table of Contents

*Use # for main sections, ## for subsections, etc.*`;
                                } else {
                                    tocMarkdown = '## Table of Contents\n\n';
                                    headers.forEach(header => {
                                        const indent = '  '.repeat(Math.max(0, header.level - 1));
                                        tocMarkdown += `${indent}- [${header.title}](#${header.anchor})\n`;
                                    });
                                }
                                
                                // Insert TOC at cursor position
                                const beforeCursor = currentText.substring(0, insertAt);
                                const afterCursor = currentText.substring(insertAt);
                                const newText = beforeCursor + '\n' + tocMarkdown + '\n' + afterCursor;
                                
                                if (activeField === 'expected') {
                                    setNewQcTest(prev => ({ ...prev, expected_value: newText }));
                                } else {
                                    setNewQcTest(prev => ({ ...prev, actual_value: newText }));
                                }
                                
                                // Position cursor after the TOC
                                setTimeout(() => {
                                    const newCursorPos = insertAt + tocMarkdown.length + 2;
                                    currentRef?.setSelectionRange(newCursorPos, newCursorPos);
                                }, 0);
                            }, 10);
                        }, 0);
                    } else if (action) {
                        finalText = cleanedText.substring(0, insertAt) + action.before + action.after + cleanedText.substring(insertAt);
                        if (activeField === 'expected') {
                            setNewQcTest(prev => ({ ...prev, expected_value: finalText }));
                        } else {
                            setNewQcTest(prev => ({ ...prev, actual_value: finalText }));
                        }
                        setTimeout(() => {
                            currentRef?.setSelectionRange(insertAt + action.before.length, insertAt + action.before.length);
                        }, 0);
                    }
                }
            }, 0);
        }
    };

    const handleKeyDown = (e, field) => {
        // Handle command menu navigation
        if (commandMenuOpen) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedCommandIndex(prev => 
                    prev < filteredCommands.length - 1 ? prev + 1 : 0
                );
                return;
            }
            
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedCommandIndex(prev => 
                    prev > 0 ? prev - 1 : filteredCommands.length - 1
                );
                return;
            }
            
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                if (filteredCommands[selectedCommandIndex]) {
                    executeCommand(filteredCommands[selectedCommandIndex]);
                }
                return;
            }
            
            if (e.key === 'Escape') {
                e.preventDefault();
                setCommandMenuOpen(false);
                return;
            }
        }

        // Handle keyboard shortcuts
        if (e.ctrlKey || e.metaKey) {
            switch (e.key.toLowerCase()) {
                case 'b':
                    e.preventDefault();
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    insertMarkdown('**', '**');
                    return;
                case 'i':
                    e.preventDefault();
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    insertMarkdown('*', '*');
                    return;
                case 'k':
                    e.preventDefault();
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    insertMarkdown('[', '](url)');
                    return;
                case '`':
                    e.preventDefault();
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    insertMarkdown('`', '`');
                    return;
                case 't':
                    e.preventDefault();
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    insertMarkdown('\n| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | Data     |\n| Row 2    | Data     | Data     |\n', '');
                    return;
                case 'h':
                    e.preventDefault();
                    setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                    insertMarkdown('\n---\n', '');
                    return;
            }
            
            // Handle Ctrl+Shift combinations
            if (e.shiftKey) {
                switch (e.key) {
                    case '*': // Ctrl+Shift+8
                        e.preventDefault();
                        setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                        insertMarkdown('\n- ', '');
                        return;
                    case '&': // Ctrl+Shift+7
                        e.preventDefault();
                        setActiveField(field === 'expected_value' ? 'expected' : 'actual');
                        insertMarkdown('\n1. ', '');
                        return;
                }
            }
        }

        // Handle Enter key for list continuation
        if (e.key === 'Enter' && !e.shiftKey) {
            const textarea = e.target;
            const value = textarea.value;
            const cursorPos = textarea.selectionStart;
            
            // Get the current line
            const lines = value.substring(0, cursorPos).split('\n');
            const currentLine = lines[lines.length - 1];
            
            // Check for bullet points
            const bulletMatch = currentLine.match(/^(\s*)-\s/);
            if (bulletMatch) {
                e.preventDefault();
                const indent = bulletMatch[1];
                const newText = value.substring(0, cursorPos) + '\n' + indent + '- ' + value.substring(cursorPos);
                if (field === 'expected_value') {
                    setNewQcTest(prev => ({ ...prev, expected_value: newText }));
                } else {
                    setNewQcTest(prev => ({ ...prev, actual_value: newText }));
                }
                
                setTimeout(() => {
                    const newCursorPos = cursorPos + indent.length + 3;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
                return;
            }
            
            // Check for numbered lists
            const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s/);
            if (numberMatch) {
                e.preventDefault();
                const indent = numberMatch[1];
                const nextNumber = parseInt(numberMatch[2]) + 1;
                const newText = value.substring(0, cursorPos) + '\n' + indent + nextNumber + '. ' + value.substring(cursorPos);
                if (field === 'expected_value') {
                    setNewQcTest(prev => ({ ...prev, expected_value: newText }));
                } else {
                    setNewQcTest(prev => ({ ...prev, actual_value: newText }));
                }
                
                setTimeout(() => {
                    const newCursorPos = cursorPos + indent.length + nextNumber.toString().length + 3;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                }, 0);
                return;
            }
        }
    };

    const getCurrentTestTypes = () => {
        return qcCategories[newQcTest.category]?.testTypes || [];
    };

    // Stepper steps
    const steps = [
        'Test Setup',
        'Test Criteria', 
        'Equipment',
        'Confirmation'
    ];

    const validateStep = (step) => {
        switch (step) {
            case 0:
                return newQcTest.category && 
                       (isCustomTest ? customTestName.trim() : newQcTest.test_type) &&
                       (newQcTest.category !== 'supplies' || customSupplyType.trim());
            case 1:
                return newQcTest.expected_value.trim() && 
                       newQcTest.actual_value.trim() && 
                       newQcTest.tested_by.trim() &&
                       newQcTest.tested_at;
            case 2:
                return true; // Equipment step is optional
            case 3:
                return (newQcTest.pass_fail_status !== 'FAIL') || newQcTest.deviation_notes.trim();
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    // Template Management Functions
    const loadTemplates = async (type) => {
        setLoadingTemplates(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const { data: templatesData, error } = await supabase.rpc('get_qc_markdown_templates', {
                p_company_id: companyData.id,
                p_template_type: type,
                p_template_category: newQcTest.category
            });

            if (error) throw error;
            setTemplates(templatesData || []);
        } catch (error) {
            console.error('Error loading templates:', error);
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const openTemplateDialog = (type) => {
        setTemplateType(type);
        loadTemplates(type);
        setShowTemplateDialog(true);
    };

    const openSaveTemplateDialog = (type) => {
        setTemplateType(type);
        setNewTemplateName('');
        setShowSaveTemplateDialog(true);
    };

    const handleLoadTemplate = (template) => {
        if (templateType === 'specification') {
            setNewQcTest(prev => ({ ...prev, expected_value: template.markdown_content }));
        } else {
            setNewQcTest(prev => ({ ...prev, actual_value: template.markdown_content }));
        }
        setShowTemplateDialog(false);
        setSelectedTemplate(null);
    };

    const handleSaveTemplate = async () => {
        if (!newTemplateName.trim()) return;

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const markdownContent = templateType === 'specification' 
                ? newQcTest.expected_value 
                : newQcTest.actual_value;

            const { error } = await supabase.rpc('save_qc_markdown_template', {
                p_company_id: companyData.id,
                p_template_name: newTemplateName.trim(),
                p_template_type: templateType,
                p_template_category: newQcTest.category,
                p_markdown_content: markdownContent,
                p_is_default: false
            });

            if (error) throw error;

            setShowSaveTemplateDialog(false);
            setNewTemplateName('');
            setSnackbar({ open: true, message: 'Template saved successfully!', severity: 'success' });
        } catch (error) {
            console.error('Error saving template:', error);
            setSnackbar({ open: true, message: `Failed to save template: ${error.message}`, severity: 'error' });
        }
    };

    const openDeleteTemplateDialog = (template) => {
        setTemplateToDelete(template);
        setShowDeleteTemplateDialog(true);
    };

    const handleDeleteTemplate = async () => {
        if (!templateToDelete) return;

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const { error } = await supabase.rpc('delete_qc_markdown_template', {
                p_template_id: templateToDelete.id,
                p_company_id: companyData.id
            });

            if (error) throw error;

            setShowDeleteTemplateDialog(false);
            setTemplateToDelete(null);
            setSnackbar({ open: true, message: 'Template deleted successfully!', severity: 'success' });
            
            // Reload templates
            loadTemplates(templateType);
        } catch (error) {
            console.error('Error deleting template:', error);
            setSnackbar({ open: true, message: `Failed to delete template: ${error.message}`, severity: 'error' });
        }
    };

    // Load available equipment
    const loadAvailableEquipment = async () => {
        setLoadingEquipment(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            // Get equipment with department information
            const { data: equipmentData, error: equipmentError } = await supabase
                .from('equipment')
                .select(`
                    id,
                    equipment_name,
                    equipment_number,
                    make,
                    year_of_installation,
                    rated_capacity,
                    remarks,
                    departments!inner (
                        id,
                        name
                    )
                `)
                .eq('company_id', companyData.id)
                .eq('is_active', true)
                .order('equipment_name', { ascending: true });
            
            if (equipmentError) throw equipmentError;
            
            const formattedEquipment = equipmentData?.map(eq => ({
                ...eq,
                department_name: eq.departments?.name || 'Unknown Department'
            })) || [];

            setAvailableEquipment(formattedEquipment);
            setFilteredEquipment(formattedEquipment);
        } catch (error) {
            console.error('Error loading equipment:', error);
            setAvailableEquipment([]);
            setFilteredEquipment([]);
        } finally {
            setLoadingEquipment(false);
        }
    };

    // Open equipment selection dialog
    const openEquipmentDialog = (index = null) => {
        setCurrentEquipmentIndex(index);
        loadAvailableEquipment();
        setShowEquipmentDialog(true);
    };

    // Handle equipment selection from list
    const handleEquipmentSelect = (equipment) => {
        setSelectedEquipmentFromList(equipment);
    };

    // Confirm equipment selection
    const confirmEquipmentSelection = () => {
        if (!selectedEquipmentFromList) return;
        
        if (currentEquipmentIndex !== null) {
            // Update existing equipment in list
            updateEquipment(equipmentList[currentEquipmentIndex].id, 'equipment_name', selectedEquipmentFromList.equipment_name);
            updateEquipment(equipmentList[currentEquipmentIndex].id, 'equipment_number', selectedEquipmentFromList.equipment_number || '');
        } else {
            // Add new equipment to list
            const newEquipment = {
                id: Date.now().toString(),
                equipment_name: selectedEquipmentFromList.equipment_name,
                equipment_number: selectedEquipmentFromList.equipment_number || '',
                usage_description: ''
            };
            setEquipmentList([...equipmentList, newEquipment]);
        }
        
        setShowEquipmentDialog(false);
        setEquipmentSearchTerm('');
        setSelectedEquipmentFromList(null);
        setCurrentEquipmentIndex(null);
    };

    // Equipment management functions
    const addEquipment = () => {
        const newEquipment = {
            id: Date.now().toString(),
            equipment_name: '',
            equipment_number: '',
            usage_description: ''
        };
        setEquipmentList([...equipmentList, newEquipment]);
    };

    const updateEquipment = (id, field, value) => {
        setEquipmentList(equipmentList.map(eq => 
            eq.id === id ? { ...eq, [field]: value } : eq
        ));
    };

    const removeEquipment = (id) => {
        setEquipmentList(equipmentList.filter(eq => eq.id !== id));
    };

    const handleReset = () => {
        setActiveStep(0);
        setIsCustomTest(false);
        setCustomTestName('');
        setEditMode(false);
        setEditingTestId(null);
        setNewQcTest({
            category: 'bottles',
            test_name: 'Visual Inspection - Clarity/Color',
            test_type: 'bottle_visual',
            expected_value: '',
            actual_value: '',
            unit_of_measure: '',
            pass_fail_status: 'PENDING',
            tested_at: dayjs(),
            tested_by: '',
            notes: '',
            batch_number: '',
            lot_number: '',
            received_date: dayjs(),
            sample_size: '',
            sample_id: '',
            deviation_notes: '',
            material_name: '',
            material_supplier: '',
            supplier_id: null
        });
        setCustomSupplyType('');
        setEquipmentList([]);
        setErrors({});
    };

    const deleteQcTestFromDb = async (testId) => {
        try {
            // Get current user's company
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const { data, error } = await supabase.rpc('delete_inventory_qc_test', {
                p_test_id: testId,
                p_company_id: companyData.id
            });

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Error deleting inventory QC test:', error);
            throw error;
        }
    };

    // Load available lot/batch data based on category
    const loadLotBatchData = async (category) => {
        setLoadingLotBatches(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            let lotBatchData = [];
            
            switch (category) {
                case 'bottles':
                    const { data: bottleData, error: bottleError } = await supabase
                        .from('bottle_inventory_items')
                        .select(`
                            id,
                            lot_number,
                            batch_number,
                            current_quantity,
                            original_quantity,
                            created_at,
                            bottles!inner (
                                id,
                                name,
                                sku,
                                suppliers (
                                    id,
                                    name
                                )
                            ),
                            locations!inner (
                                id,
                                name
                            )
                        `)
                        .eq('company_id', companyData.id)
                        .eq('is_active', true)
                        .gt('current_quantity', 0)
                        .order('created_at', { ascending: false });
                    
                    if (bottleError) throw bottleError;
                    lotBatchData = bottleData?.map(item => ({
                        ...item,
                        item_type: 'bottles',
                        item_name: item.bottles.name,
                        item_sku: item.bottles.sku,
                        location_name: item.locations.name,
                        supplier_name: item.bottles.suppliers?.name || 'Unknown Supplier',
                        supplier_id: item.bottles.suppliers?.id || null
                    })) || [];
                    break;

                case 'caps':
                    const { data: capData, error: capError } = await supabase
                        .from('caps_inventory_items')
                        .select(`
                            id,
                            lot_number,
                            batch_number,
                            current_quantity,
                            original_quantity,
                            created_at,
                            caps!inner (
                                id,
                                name,
                                sku,
                                suppliers (
                                    id,
                                    name
                                )
                            ),
                            locations!inner (
                                id,
                                name
                            )
                        `)
                        .eq('company_id', companyData.id)
                        .eq('is_active', true)
                        .gt('current_quantity', 0)
                        .order('created_at', { ascending: false });
                    
                    if (capError) throw capError;
                    lotBatchData = capData?.map(item => ({
                        ...item,
                        item_type: 'caps',
                        item_name: item.caps.name,
                        item_sku: item.caps.sku,
                        location_name: item.locations.name,
                        supplier_name: item.caps.suppliers?.name || 'Unknown Supplier',
                        supplier_id: item.caps.suppliers?.id || null
                    })) || [];
                    break;

                case 'labels':
                    const { data: labelData, error: labelError } = await supabase
                        .from('label_inventory_items')
                        .select(`
                            id,
                            lot_number,
                            batch_number,
                            current_quantity,
                            original_quantity,
                            created_at,
                            labels!inner (
                                id,
                                name,
                                sku,
                                suppliers (
                                    id,
                                    name
                                )
                            ),
                            locations!inner (
                                id,
                                name
                            )
                        `)
                        .eq('company_id', companyData.id)
                        .eq('is_active', true)
                        .gt('current_quantity', 0)
                        .order('created_at', { ascending: false });
                    
                    if (labelError) throw labelError;
                    lotBatchData = labelData?.map(item => ({
                        ...item,
                        item_type: 'labels',
                        item_name: item.labels.name,
                        item_sku: item.labels.sku,
                        location_name: item.locations.name,
                        supplier_name: item.labels.suppliers?.name || 'Unknown Supplier',
                        supplier_id: item.labels.suppliers?.id || null
                    })) || [];
                    break;

                case 'supplies':
                    const { data: supplyData, error: supplyError } = await supabase
                        .from('supply_inventory_items')
                        .select(`
                            id,
                            lot_number,
                            batch_number,
                            current_quantity,
                            original_quantity,
                            created_at,
                            supplies!inner (
                                id,
                                name,
                                sku,
                                suppliers (
                                    id,
                                    name
                                )
                            ),
                            locations!inner (
                                id,
                                name
                            )
                        `)
                        .eq('company_id', companyData.id)
                        .eq('is_active', true)
                        .gt('current_quantity', 0)
                        .order('created_at', { ascending: false });
                    
                    if (supplyError) throw supplyError;
                    lotBatchData = supplyData?.map(item => ({
                        ...item,
                        item_type: 'supplies',
                        item_name: item.supplies.name,
                        item_sku: item.supplies.sku,
                        location_name: item.locations.name,
                        supplier_name: item.supplies.suppliers?.name || 'Unknown Supplier',
                        supplier_id: item.supplies.suppliers?.id || null
                    })) || [];
                    break;

                default:
                    break;
            }

            setAvailableLotBatches(lotBatchData);
            setFilteredLotBatches(lotBatchData);
        } catch (error) {
            console.error('Error loading lot/batch data:', error);
        } finally {
            setLoadingLotBatches(false);
        }
    };

    // Filter lot/batch data based on search term
    useEffect(() => {
        if (!lotBatchSearchTerm) {
            setFilteredLotBatches(availableLotBatches);
        } else {
            const filtered = availableLotBatches.filter(item => 
                item.lot_number?.toLowerCase().includes(lotBatchSearchTerm.toLowerCase()) ||
                item.batch_number?.toLowerCase().includes(lotBatchSearchTerm.toLowerCase()) ||
                item.item_name?.toLowerCase().includes(lotBatchSearchTerm.toLowerCase()) ||
                item.item_sku?.toLowerCase().includes(lotBatchSearchTerm.toLowerCase()) ||
                item.location_name?.toLowerCase().includes(lotBatchSearchTerm.toLowerCase())
            );
            setFilteredLotBatches(filtered);
        }
    }, [lotBatchSearchTerm, availableLotBatches]);

    // Filter equipment based on search term
    useEffect(() => {
        if (!equipmentSearchTerm) {
            setFilteredEquipment(availableEquipment);
        } else {
            const filtered = availableEquipment.filter(eq => 
                eq.equipment_name?.toLowerCase().includes(equipmentSearchTerm.toLowerCase()) ||
                eq.equipment_number?.toLowerCase().includes(equipmentSearchTerm.toLowerCase()) ||
                eq.make?.toLowerCase().includes(equipmentSearchTerm.toLowerCase()) ||
                eq.department_name?.toLowerCase().includes(equipmentSearchTerm.toLowerCase())
            );
            setFilteredEquipment(filtered);
        }
    }, [equipmentSearchTerm, availableEquipment]);

    // Handle lot/batch selection
    const handleLotBatchSelect = (lotBatch) => {
        setSelectedLotBatch(lotBatch);
        setNewQcTest(prev => ({
            ...prev,
            batch_number: lotBatch.batch_number || '',
            lot_number: lotBatch.lot_number || '',
            material_name: lotBatch.item_name || '',
            material_supplier: lotBatch.supplier_name || '',
            supplier_id: lotBatch.supplier_id || null
        }));
        setShowLotBatchDialog(false);
        setLotBatchSearchTerm('');
        setSelectedLotBatch(null);
    };

    // Open lot/batch selection dialog
    const openLotBatchDialog = () => {
        loadLotBatchData(newQcTest.category);
        setShowLotBatchDialog(true);
    };

    const handleDeleteQcTest = (test) => {
        setTestToDelete(test);
        setShowDeleteDialog(true);
    };

    const confirmDeleteQcTest = async () => {
        if (!testToDelete) return;
        
        setDeleting(true);
        try {
            await deleteQcTestFromDb(testToDelete.id);
            await loadQcTests();
            setShowDeleteDialog(false);
            setTestToDelete(null);
        } catch (error) {
            console.error('Error deleting QC test:', error);
            alert(`Failed to delete QC test: ${error.message}`);
        } finally {
            setDeleting(false);
        }
    };

    const handleViewTest = (test) => {
        setSelectedTest(test);
        setShowViewDialog(true);
    };

    const handleEditTest = async (test) => {
        setEditMode(true);
        setEditingTestId(test.id);
        setNewQcTest({
            ...test,
            tested_at: dayjs(test.tested_at),
            received_date: test.received_date ? dayjs(test.received_date) : dayjs()
        });
        setIsCustomTest(test.is_custom_test || false);
        if (test.is_custom_test) {
            setCustomTestName(test.test_name);
        }
        if (test.category === 'supplies' && test.custom_supply_type) {
            setCustomSupplyType(test.custom_supply_type);
        }
        
        // Load equipment data for this test
        try {
            const { data: equipmentData, error } = await supabase.rpc('get_inventory_qc_test_equipment', {
                p_qc_test_id: test.id
            });
            
            if (error) throw error;
            
            const formattedEquipment = equipmentData.map(eq => ({
                id: eq.id,
                equipment_name: eq.equipment_name,
                equipment_number: eq.equipment_number,
                usage_description: eq.usage_description
            }));
            
            setEquipmentList(formattedEquipment);
        } catch (error) {
            console.error('Error loading equipment data:', error);
            setEquipmentList([]);
        }
        
        setActiveStep(1);
        setShowAddDialog(true);
    };

    const handleReviewTest = (test) => {
        setTestUnderReview(test);
        setReviewerName('');
        setReviewerRole('qa_manager');
        setReviewComments('');
        setSignaturePreview('');
        setShowReviewDialog(true);
    };

    const handleUnapproveTest = (test) => {
        setTestToUnapprove(test);
        setUnapproveReason('');
        setUnapproveSignature('');
        setUnapproveSignaturePreview('');
        setShowUnapproveDialog(true);
    };

    const handleQAReview = async () => {
        if (!testUnderReview) return;
        
        setReviewing(true);
        try {
            // Get current user's company
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const { data, error } = await supabase.rpc('approve_inventory_qc_test', {
                p_test_id: testUnderReview.id,
                p_company_id: companyData.id,
                p_reviewer_name: reviewerName,
                p_reviewer_role: reviewerRole,
                p_review_comments: reviewComments || null
            });

            if (error) throw error;
            
            await loadQcTests();
            setShowReviewDialog(false);
            setTestUnderReview(null);
            
        } catch (error) {
            console.error('Error reviewing QC test:', error);
            alert(`Failed to review QC test: ${error.message}`);
        } finally {
            setReviewing(false);
        }
    };

    const handleUnapproveQA = async () => {
        if (!testToUnapprove) return;
        
        setUnapproving(true);
        try {
            // Get current user's company
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { data: companyData } = await supabase
                .from('companies')
                .select('id')
                .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
                .single();

            if (!companyData) throw new Error('No company found for user');

            const { data, error } = await supabase.rpc('unapprove_inventory_qc_test', {
                p_test_id: testToUnapprove.id,
                p_company_id: companyData.id,
                p_unapprover_name: unapproveSignature,
                p_unapprove_reason: unapproveReason
            });

            if (error) throw error;
            
            await loadQcTests();
            setShowUnapproveDialog(false);
            setTestToUnapprove(null);
            
        } catch (error) {
            console.error('Error unapproving QC test:', error);
            alert(`Failed to unapprove QC test: ${error.message}`);
        } finally {
            setUnapproving(false);
        }
    };

    const handleAddQcTest = async () => {
        setSaving(true);
        try {
            const finalTestName = isCustomTest ? customTestName : newQcTest.test_name;
            const finalTestType = isCustomTest ? 'custom' : newQcTest.test_type;

            const testData = {
                ...newQcTest,
                test_name: finalTestName,
                test_type: finalTestType,
                tested_at: newQcTest.tested_at.toISOString(),
                received_date: newQcTest.received_date ? newQcTest.received_date.toISOString() : null,
                custom_supply_type: newQcTest.category === 'supplies' ? customSupplyType : null,
                is_custom_test: isCustomTest,
                id: editMode ? editingTestId : null
            };

            const testId = await saveQcTest(testData);
            
            // Save equipment if any
            if (equipmentList.length > 0) {
                const equipmentData = equipmentList.map(eq => ({
                    equipment_name: eq.equipment_name,
                    equipment_number: eq.equipment_number,
                    usage_description: eq.usage_description
                }));
                
                const { error: equipmentError } = await supabase.rpc('save_inventory_qc_test_equipment', {
                    p_qc_test_id: testId,
                    p_equipment_data: equipmentData
                });
                
                if (equipmentError) {
                    console.error('Error saving equipment:', equipmentError);
                    throw new Error(`Failed to save equipment: ${equipmentError.message}`);
                }
            }
            
            await loadQcTests();
            
            handleReset();
            setShowAddDialog(false);
        } catch (error) {
            console.error(`Error ${editMode ? 'updating' : 'adding'} inventory QC test:`, error);
            alert(`Failed to ${editMode ? 'update' : 'save'} inventory QC test: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaveSuccess(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving quality checks:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PASS': return theme.palette.success.main;
            case 'FAIL': return theme.palette.error.main;
            case 'PENDING': return theme.palette.warning.main;
            case 'PENDING_REVIEW': return theme.palette.info.main;
            default: return theme.palette.grey[500];
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PASS': return <CheckCircleIcon />;
            case 'FAIL': return <CancelIcon />;
            case 'PENDING': return <WarningIcon />;
            case 'PENDING_REVIEW': return <AssignmentIcon />;
            default: return <WarningIcon />;
        }
    };

    const passedTests = qualityChecks.filter(test => test.pass_fail_status === 'PASS').length;
    const totalTests = qualityChecks.length;
    const passRate = totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0;

    // Permission check
    if (!hasPermission('inventory.supplies.qc_tests.view')) {
        return (
            <Box sx={{ p: 2 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: '70vh',
                    textAlign: 'center'
                }}>
                    <Card sx={{
                        p: 8,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                        maxWidth: 600,
                        width: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'default',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                            opacity: 0.8,
                            transition: 'opacity 0.3s ease'
                        },
                        '&:hover::before': {
                            opacity: 1
                        }
                    }}>
                        <Box sx={{ 
                            p: 3, 
                            borderRadius: 3, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            mb: 6,
                            display: 'inline-flex',
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                        }}>
                            <SpeedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h3" sx={{ 
                            mb: 3, 
                            fontWeight: 500, 
                            color: 'text.primary',
                            fontSize: '2.5rem',
                            letterSpacing: '-0.8px'
                        }}>
                            Access Restricted
                        </Typography>
                        <Typography variant="h6" sx={{ 
                            color: 'text.secondary', 
                            mb: 4,
                            fontWeight: 500,
                            fontSize: '1.2rem'
                        }}>
                            Supply Quality Check Permissions Required
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.7,
                            mb: 6,
                            fontSize: '1.05rem',
                            maxWidth: 450,
                            mx: 'auto'
                        }}>
                            You don't currently have the necessary permissions to access Supply Quality Control Tests. 
                            Contact your system administrator to request access or verify your role permissions.
                        </Typography>
                    </Card>
                </Box>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 2 }}>
            {/* Success Message */}
            {saveSuccess && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 4,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.success.main}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        fontWeight: 500
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        Inventory quality checks saved successfully!
                    </Typography>
                </Alert>
            )}

            {/* QC Overview Dashboard */}
            <Card sx={{ 
                mb: 2,
                borderRadius: 2, 
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }
            }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                            p: 1, 
                            borderRadius: 1.5, 
                            background: `linear-gradient(${alpha(theme.palette.info.main, 0.2)}, ${alpha(theme.palette.info.main, 0.1)})`,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                            mr: 1.5
                        }}>
                            <SpeedIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.25 }}>
                                Supply Quality Control Dashboard
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Monitor and record quality test results for incoming supplies
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.info.main, lineHeight: 1 }}>
                                {passRate}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Pass Rate
                            </Typography>
                        </Box>
                    </Box>
                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={2.4}>
                                            <Box sx={{ textAlign: 'center', p: 1.5 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                                                    {passedTests}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    Tests Passed
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={2.4}>
                                            <Box sx={{ textAlign: 'center', p: 1.5 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                                                    {qualityChecks.filter(test => test.pass_fail_status === 'FAIL').length}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    Tests Failed
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={2.4}>
                                            <Box sx={{ textAlign: 'center', p: 1.5 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                                                    {qualityChecks.filter(test => test.pass_fail_status === 'PENDING').length}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    Tests Pending
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={2.4}>
                                            <Box sx={{ textAlign: 'center', p: 1.5 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.info.main }}>
                                                    {qualityChecks.filter(test => (test.qa_review_status || 'PENDING') === 'PENDING').length}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    Pending QA Review
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={2.4}>
                                            <Box sx={{ textAlign: 'center', p: 1.5 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.success.dark }}>
                                                    {qualityChecks.filter(test => (test.qa_review_status || 'PENDING') === 'APPROVED').length}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    QA Approved
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={2}>
                {/* Quality Test Records */}
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 2, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                    }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        p: 1, 
                                        borderRadius: 1.5, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        mr: 1.5
                                    }}>
                                        <QualityControlIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Supply Quality Test Records
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Add and manage quality control tests for incoming supplies
                                        </Typography>
                                    </Box>
                                </Box>
                                {canEdit && (
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setEditMode(false);
                                            setEditingTestId(null);
                                            handleReset();
                                            setShowAddDialog(true);
                                        }}
                                        size="small"
                                        sx={{
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1,
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                            color: 'white',
                                            fontWeight: 500,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        Add QC Test
                                    </Button>
                                )}
                            </Box>
                            
                            {/* Search and Filter Controls */}
                                {qualityChecks.length > 0 && (
                                    <Card sx={{
                                        mb: 2,
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.08)}`
                                    }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                <TextField
                                                    placeholder="Search tests..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    size="small"
                                                    sx={{ 
                                                        minWidth: 280,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'white'
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SearchIcon sx={{ color: theme.palette.primary.main }} />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                    <InputLabel>Filter by Category</InputLabel>
                                                    <Select
                                                        value={filterCategory}
                                                        label="Filter by Category"
                                                        onChange={(e) => setFilterCategory(e.target.value)}
                                                        sx={{ backgroundColor: 'white' }}
                                                    >
                                                        <MenuItem value="all">All Categories</MenuItem>
                                                        {Object.entries(qcCategories).map(([key, category]) => (
                                                            <MenuItem key={key} value={key}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {React.createElement(category.icon, { sx: { fontSize: 18 } })}
                                                                    {category.name}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                
                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                    <InputLabel>QA Review Status</InputLabel>
                                                    <Select
                                                        value={filterQAStatus}
                                                        label="QA Review Status"
                                                        onChange={(e) => setFilterQAStatus(e.target.value)}
                                                        sx={{ backgroundColor: 'white' }}
                                                    >
                                                        <MenuItem value="all">All QA Status</MenuItem>
                                                        <MenuItem value="PENDING">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <WarningIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                                                                Pending Review
                                                            </Box>
                                                        </MenuItem>
                                                        <MenuItem value="APPROVED">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                                                                Approved
                                                            </Box>
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                                
                                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                                    <InputLabel>Test Results</InputLabel>
                                                    <Select
                                                        value={filterTestStatus}
                                                        label="Test Results"
                                                        onChange={(e) => setFilterTestStatus(e.target.value)}
                                                        sx={{ backgroundColor: 'white' }}
                                                    >
                                                        <MenuItem value="all">All Results</MenuItem>
                                                        <MenuItem value="PASS">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                                                                Pass
                                                            </Box>
                                                        </MenuItem>
                                                        <MenuItem value="FAIL">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CancelIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                                                                Fail
                                                            </Box>
                                                        </MenuItem>
                                                        <MenuItem value="PENDING">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <WarningIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                                                                Pending
                                                            </Box>
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                                
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => {
                                                        setSearchTerm('');
                                                        setFilterCategory('all');
                                                        setFilterQAStatus('all');
                                                        setFilterTestStatus('all');
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1,
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                        backgroundColor: 'white'
                                                    }}
                                                >
                                                    Clear Filters
                                                </Button>
                                                
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2, mt: 0.5, alignSelf: 'center' }}>
                                                    {filteredQualityChecks.length} test{filteredQualityChecks.length !== 1 ? 's' : ''}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            
                            {filteredQualityChecks.length === 0 && qualityChecks.length > 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <FilterListIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
                                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                        No Matching Tests
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Try adjusting your search or filter criteria
                                    </Typography>
                                </Box>
                            ) : qualityChecks.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
                                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                                        No Supply Quality Tests Added
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Click "Add QC Test" to start recording quality control checks for incoming supplies
                                    </Typography>
                                </Box>
                            ) : (
                                <Paper sx={{ 
                                    borderRadius: 3, 
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    overflow: 'hidden',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '@keyframes slideInUp': {
                                        '0%': {
                                            transform: 'translateY(100%)',
                                            opacity: 0,
                                        },
                                        '100%': {
                                            transform: 'translateY(0)',
                                            opacity: 1,
                                        }
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <TableContainer sx={{ overflow: 'visible' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Material Name</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Supplier</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Category</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Test Name</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Status</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Batch</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Lot</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Tested By</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>Date/Time</TableCell>
                                                    <TableCell sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5
                                                    }}>QA Review</TableCell>
                                                    <TableCell align="center" sx={{ 
                                                        fontWeight: 500,
                                                        fontSize: '0.9rem',
                                                        bgcolor: 'white',
                                                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                        color: 'primary.main',
                                                        py: 1.5,
                                                        px: 2.5,
                                                        width: 120
                                                    }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(rowsPerPage > 0
                                                    ? filteredQualityChecks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    : filteredQualityChecks
                                                ).map((test) => (
                                                    <TableRow 
                                                        key={test.id} 
                                                        hover
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': { 
                                                                bgcolor: 'grey.50',
                                                                transform: 'translateX(4px)',
                                                                transition: 'all 0.2s ease',
                                                                boxShadow: 'inset 4px 0 0 black'
                                                            },
                                                            '& .MuiTableCell-root': {
                                                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                                                py: 2,
                                                                px: 2.5
                                                            }
                                                        }}
                                                        onClick={() => handleViewTest(test)}
                                                    >
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {test.material_name || 'Unknown Material'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {test.material_supplier || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    {React.createElement(qcCategories[test.category]?.icon, { 
                                                                        sx: { fontSize: 18, mr: 1, color: theme.palette.primary.main } 
                                                                    })}
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {qcCategories[test.category]?.name || test.category}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {test.test_name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                icon={getStatusIcon(test.pass_fail_status)}
                                                                label={test.pass_fail_status}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: alpha(getStatusColor(test.pass_fail_status), 0.1),
                                                                    color: getStatusColor(test.pass_fail_status),
                                                                    border: `1px solid ${getStatusColor(test.pass_fail_status)}`,
                                                                    fontWeight: 500
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {test.batch_number || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {test.lot_number || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {test.tested_by}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {dayjs(test.tested_at).format('MM/DD HH:mm')}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                <Chip
                                                                    label={test.qa_review_status || 'PENDING'}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: test.qa_review_status === 'APPROVED' 
                                                                            ? alpha(theme.palette.success.main, 0.1)
                                                                            : alpha(theme.palette.warning.main, 0.1),
                                                                        color: test.qa_review_status === 'APPROVED' 
                                                                            ? theme.palette.success.main
                                                                            : theme.palette.warning.main,
                                                                        border: `1px solid ${test.qa_review_status === 'APPROVED' 
                                                                            ? theme.palette.success.main
                                                                            : theme.palette.warning.main}`,
                                                                        fontWeight: 500,
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                />
                                                                {test.qa_reviewed_by_name && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        by {test.qa_reviewed_by_name}
                                                                    </Typography>
                                                                )}
                                                                {test.qa_review_datetime && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        {dayjs(test.qa_review_datetime).format('MM/DD HH:mm')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                                <Tooltip title={!canEdit ? 'No edit permission' : test.qa_review_status === 'APPROVED' ? 'Cannot edit approved test' : 'Edit'}>
                                                                    <IconButton
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (canEdit && test.qa_review_status !== 'APPROVED') {
                                                                                handleEditTest(test);
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                        disabled={!canEdit || test.qa_review_status === 'APPROVED'}
                                                                        sx={{
                                                                            mr: 1,
                                                                            color: (!canEdit || test.qa_review_status === 'APPROVED') ? 'text.disabled' : 'text.primary',
                                                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                            borderRadius: 2,
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                bgcolor: (!canEdit || test.qa_review_status === 'APPROVED') ? 'transparent' : 'primary.main',
                                                                                color: (!canEdit || test.qa_review_status === 'APPROVED') ? 'text.disabled' : 'white',
                                                                                transform: (!canEdit || test.qa_review_status === 'APPROVED') ? 'none' : 'scale(1.05)',
                                                                                boxShadow: (!canEdit || test.qa_review_status === 'APPROVED') ? 'none' : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                                                            },
                                                                            '&:disabled': {
                                                                                color: 'text.disabled',
                                                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                                                                                transform: 'none',
                                                                                boxShadow: 'none'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <EditTestIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={!canApprove ? 'No approve permission' : 'QA Review'}>
                                                                    <IconButton
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (canApprove) {
                                                                                handleReviewTest(test);
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                        disabled={!canApprove}
                                                                        sx={{
                                                                            color: !canApprove ? 'text.disabled' : test.qa_review_status === 'APPROVED' ? 'success.main' : 'warning.main',
                                                                            border: `1px solid ${alpha(!canApprove ? theme.palette.grey[300] : test.qa_review_status === 'APPROVED' ? theme.palette.success.main : theme.palette.warning.main, 0.3)}`,
                                                                            borderRadius: 2,
                                                                            '&:hover': {
                                                                                bgcolor: !canApprove ? 'transparent' : test.qa_review_status === 'APPROVED' ? 'success.main' : 'warning.main',
                                                                                color: !canApprove ? 'text.disabled' : 'white'
                                                                            },
                                                                            '&:disabled': {
                                                                                color: 'text.disabled',
                                                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                                                                                transform: 'none',
                                                                                boxShadow: 'none'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <AssignmentIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={!canDelete ? 'No delete permission' : test.qa_review_status === 'APPROVED' ? 'Cannot delete approved test' : 'Delete'}>
                                                                    <IconButton
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (canDelete && test.qa_review_status !== 'APPROVED') {
                                                                                handleDeleteQcTest(test);
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                        disabled={!canDelete || test.qa_review_status === 'APPROVED'}
                                                                        sx={{
                                                                            color: (!canDelete || test.qa_review_status === 'APPROVED') ? 'text.disabled' : 'error.main',
                                                                            border: `1px solid ${alpha((!canDelete || test.qa_review_status === 'APPROVED') ? theme.palette.grey[300] : theme.palette.error.main, 0.3)}`,
                                                                            borderRadius: 2,
                                                                            '&:hover': {
                                                                                bgcolor: (!canDelete || test.qa_review_status === 'APPROVED') ? 'transparent' : 'error.main',
                                                                                color: (!canDelete || test.qa_review_status === 'APPROVED') ? 'text.disabled' : 'white'
                                                                            },
                                                                            '&:disabled': {
                                                                                color: 'text.disabled',
                                                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                                                                                transform: 'none',
                                                                                boxShadow: 'none'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    <Box sx={{ 
                                        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                        borderRadius: '0 0 12px 12px'
                                    }}>
                                        <TablePagination
                                            rowsPerPageOptions={[5, 10, 25]}
                                            component="div"
                                            count={filteredQualityChecks.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            sx={{ 
                                                px: 3,
                                                py: 2,
                                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    color: theme.palette.text.primary
                                                },
                                                '& .MuiTablePagination-select': {
                                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                                    borderRadius: 2,
                                                    px: 2,
                                                    py: 1,
                                                    fontWeight: 500,
                                                    fontSize: '0.9rem',
                                                    background: 'white',
                                                    '&:hover': {
                                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                                    }
                                                },
                                                '& .MuiTablePagination-actions': {
                                                    '& .MuiIconButton-root': {
                                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                                        borderRadius: 2,
                                                        margin: '0 2px',
                                                        background: 'white',
                                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            background: theme.palette.primary.main,
                                                            color: 'white',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                                        },
                                                        '&.Mui-disabled': {
                                                            background: alpha(theme.palette.grey[300], 0.5),
                                                            color: theme.palette.grey[500],
                                                            border: `1px solid ${theme.palette.grey[300]}`,
                                                            transform: 'none',
                                                            boxShadow: 'none'
                                                        }
                                                    }
                                                }
                                    }}
                                />
                            </Box>
                                </Paper>
                            )}
                        </CardContent>
                    </Card>
                </Grid>


                {/* Save Button */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<CheckCircleIcon />}
                            size="small"
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.95)',
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                fontSize: '0.85rem',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            Save Quality test
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {/* Add QC Test Dialog with Full Stepper */}
            <Dialog 
                open={showAddDialog} 
                onClose={() => {
                    setShowAddDialog(false);
                    handleReset();
                }} 
                maxWidth={false}
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        minHeight: '600px',
                        width: '95vw',
                        maxWidth: '2000px'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.05), 
                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                    fontWeight: 500
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {editMode ? 'Edit Supply Quality Control Test' : 'Add Supply Quality Control Test'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Step {activeStep + 1} of {steps.length}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Stepper activeStep={activeStep} orientation="vertical" sx={{ p: 4 }}>
                        {/* Step 1: Test Setup */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Test Setup
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Select category, test name, and test type
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Box sx={{ mt: 2, mb: 3 }}>
                                    {/* Category Selection */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                                        Quality Control Category
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {Object.entries(qcCategories).map(([key, category]) => (
                                            <Grid item xs={6} md={3} key={key}>
                                                <Card
                                                    onClick={() => {
                                                        const firstTest = category.testTypes[0];
                                                        setSelectedCategory(key); // Update selectedCategory state
                                                        setNewQcTest({
                                                            ...newQcTest,
                                                            category: key,
                                                            test_type: firstTest?.value || 'visual',
                                                            test_name: firstTest?.label || ''
                                                        });
                                                    }}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        borderRadius: 2,
                                                        border: newQcTest.category === key 
                                                            ? `2px solid ${theme.palette.primary.main}` 
                                                            : `1px solid ${theme.palette.grey[300]}`,
                                                        background: newQcTest.category === key 
                                                            ? alpha(theme.palette.primary.main, 0.1) 
                                                            : 'white',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            border: `2px solid ${theme.palette.primary.main}`
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                                                        <Box sx={{ mb: 0.5 }}>
                                                            {React.createElement(category.icon, { 
                                                                sx: { 
                                                                    fontSize: 32, 
                                                                    color: newQcTest.category === key 
                                                                        ? theme.palette.primary.main 
                                                                        : theme.palette.text.secondary 
                                                                } 
                                                            })}
                                                        </Box>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: 500,
                                                                color: newQcTest.category === key 
                                                                    ? theme.palette.primary.main 
                                                                    : theme.palette.text.primary 
                                                            }}
                                                        >
                                                            {category.name}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {/* Custom Supply Type */}
                                    {newQcTest.category === 'supplies' && (
                                        <Box sx={{ mb: 3 }}>
                                            <TextField
                                                fullWidth
                                                label="Supply Type *"
                                                value={customSupplyType}
                                                onChange={(e) => setCustomSupplyType(e.target.value)}
                                                placeholder="e.g., Packaging Tape, Cardboard Boxes, Plastic Wrap, etc."
                                                helperText="Specify the type of supply being tested"
                                                required
                                                sx={{ mb: 2 }}
                                            />
                                        </Box>
                                    )}

                                    {/* Test Type Selection */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isCustomTest}
                                                        onChange={(e) => {
                                                            setIsCustomTest(e.target.checked);
                                                            if (!e.target.checked) {
                                                                setCustomTestName('');
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Create Custom Test"
                                                sx={{ mb: 2 }}
                                            />
                                        </Grid>
                                        
                                        {isCustomTest ? (
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Custom Test Name *"
                                                    value={customTestName}
                                                    onChange={(e) => setCustomTestName(e.target.value)}
                                                    placeholder="Enter custom test name"
                                                    required
                                                />
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Predefined Test Type</InputLabel>
                                                    <Select
                                                        value={newQcTest.test_type}
                                                        onChange={(e) => {
                                                            const selectedTest = getCurrentTestTypes().find(t => t.value === e.target.value);
                                                            setNewQcTest({
                                                                ...newQcTest, 
                                                                test_type: e.target.value,
                                                                test_name: selectedTest?.label || ''
                                                            });
                                                        }}
                                                        label="Predefined Test Type"
                                                    >
                                                        {getCurrentTestTypes().map((type) => (
                                                            <MenuItem key={type.value} value={type.value}>
                                                                {type.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {/* Lot/Batch & Sample Information */}
                                    <Box sx={{ mt: 4 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                                            Lot/Batch & Sample Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<SearchIcon />}
                                                    onClick={openLotBatchDialog}
                                                    sx={{ mb: 2 }}
                                                >
                                                    Search Available Lot/Batch Numbers for {newQcTest.category}
                                                </Button>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Click to search from existing inventory or enter manually below
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Batch Number"
                                                    value={newQcTest.batch_number}
                                                    onChange={(e) => setNewQcTest({...newQcTest, batch_number: e.target.value})}
                                                    placeholder="e.g., BATCH-2024-001"
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip title="Search from inventory">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={openLotBatchDialog}
                                                                    >
                                                                        <InventoryIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Lot Number"
                                                    value={newQcTest.lot_number}
                                                    onChange={(e) => setNewQcTest({...newQcTest, lot_number: e.target.value})}
                                                    placeholder="e.g., LOT-2024-001"
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Supplier"
                                                    value={newQcTest.material_supplier}
                                                    onChange={(e) => setNewQcTest({...newQcTest, material_supplier: e.target.value})}
                                                    placeholder="e.g., Acme Supply Co."
                                                    helperText="Will be auto-filled when selecting from inventory"
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <DateTimePicker
                                                    label="Received Date"
                                                    value={newQcTest.received_date}
                                                    onChange={(newValue) => setNewQcTest({...newQcTest, received_date: newValue})}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                    ampm={false}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Sample Size"
                                                    value={newQcTest.sample_size}
                                                    onChange={(e) => setNewQcTest({...newQcTest, sample_size: e.target.value})}
                                                    placeholder="e.g., 10 units, 100ml"
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Sample ID"
                                                    value={newQcTest.sample_id}
                                                    onChange={(e) => setNewQcTest({...newQcTest, sample_id: e.target.value})}
                                                    placeholder="e.g., SMP-2024-001"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(0)}
                                        sx={{ mr: 1 }}
                                    >
                                        Continue
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Step 2: Test Criteria */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Test Criteria
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Define specifications and record results
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Box sx={{ mt: 2, mb: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            {/* Test Specifications Markdown Editor */}
                                            <Card sx={{ 
                                                borderRadius: 2, 
                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                minHeight: '300px'
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center', 
                                                    p: 2, 
                                                    pb: 0
                                                }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                        Test Specifications/What to Check *
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => openTemplateDialog('specification')}
                                                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                        >
                                                            Load Template
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => openSaveTemplateDialog('specification')}
                                                            disabled={!newQcTest.expected_value.trim()}
                                                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                        >
                                                            Save as Template
                                                        </Button>
                                                        <IconButton
                                                            onClick={() => setExpectedPreviewMode(!expectedPreviewMode)}
                                                            sx={{
                                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                                borderRadius: 2,
                                                                backgroundColor: 'white'
                                                            }}
                                                        >
                                                            {expectedPreviewMode ? <EditIcon /> : <PreviewIcon />}
                                                        </IconButton>
                                                    </Box>
                                                </Box>

                                                {!expectedPreviewMode && (
                                                    <MarkdownToolbar onInsert={(before, after) => {
                                                        setActiveField('expected');
                                                        insertMarkdown(before, after);
                                                    }} />
                                                )}

                                                <Box sx={{ 
                                                    position: 'relative', 
                                                    height: '800px',
                                                    display: 'flex',
                                                    overflow: 'hidden'
                                                }}>
                                                    <Box sx={{ 
                                                        width: expectedPreviewMode ? '0%' : (newQcTest.expected_value ? '50%' : '100%'),
                                                        height: '100%',
                                                        transition: 'width 0.3s ease',
                                                        opacity: expectedPreviewMode ? 0 : 1
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            value={newQcTest.expected_value}
                                                            onChange={(e) => handleInputChange('expected_value', e.target.value)}
                                                            onKeyDown={(e) => handleKeyDown(e, 'expected_value')}
                                                            onFocus={() => setActiveField('expected')}
                                                            placeholder="Describe what needs to be tested and the acceptance criteria..."
                                                            inputRef={setExpectedValueRef}
                                                            sx={{
                                                                height: '100%',
                                                                '& .MuiInputBase-root': { height: '100%', alignItems: 'stretch' },
                                                                '& .MuiInputBase-inputMultiline': { height: '100% !important', resize: 'none' },
                                                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                                                            }}
                                                            InputProps={{
                                                                sx: {
                                                                    fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                                                    fontSize: '0.9rem',
                                                                    padding: 2
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    
                                                    {(newQcTest.expected_value || expectedPreviewMode) && (
                                                        <Box sx={{ 
                                                            width: expectedPreviewMode ? '100%' : '50%',
                                                            height: '100%',
                                                            borderLeft: expectedPreviewMode ? 'none' : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                            overflow: 'auto',
                                                            backgroundColor: alpha(theme.palette.grey[50], 0.3),
                                                            p: 2
                                                        }}>
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                                                h1: ({ children }) => <h1 style={{ fontSize: '1.5rem', fontWeight: 500, margin: '24px 0 16px 0', color: theme.palette.text.primary, borderBottom: `2px solid ${theme.palette.primary.main}`, paddingBottom: '8px' }}>{children}</h1>,
                                                                h2: ({ children }) => <h2 style={{ fontSize: '1.25rem', fontWeight: 500, margin: '20px 0 12px 0', color: theme.palette.text.primary, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, paddingBottom: '6px' }}>{children}</h2>,
                                                                h3: ({ children }) => <h3 style={{ fontSize: '1.1rem', fontWeight: 500, margin: '16px 0 8px 0', color: theme.palette.text.primary }}>{children}</h3>,
                                                                p: ({ children }) => <p style={{ margin: '8px 0', lineHeight: '1.6', color: theme.palette.text.primary }}>{children}</p>,
                                                                ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ul>,
                                                                ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ol>,
                                                                li: ({ children }) => (
                                                                        <li style={{
                                                                            margin: '4px 0',
                                                                            lineHeight: '1.5',
                                                                            color: theme.palette.text.primary
                                                                        }}>
                                                                            {children}
                                                                        </li>
                                                                ),
                                                                blockquote: ({ children }) => <blockquote style={{ margin: '16px 0', padding: '12px 16px', borderLeft: `4px solid ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: '0 4px 4px 0', fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</blockquote>,
                                                                table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', margin: '16px 0', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>{children}</table>,
                                                                thead: ({ children }) => <thead style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8) }}>{children}</thead>,
                                                                th: ({ children }) => <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, backgroundColor: alpha(theme.palette.grey[100], 0.8), color: theme.palette.text.primary }}>{children}</th>,
                                                                td: ({ children }) => <td style={{ padding: '12px', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, color: theme.palette.text.primary }}>{children}</td>,
                                                                hr: () => <hr style={{ margin: '24px 0', border: 'none', height: '2px', backgroundColor: alpha(theme.palette.grey[400], 0.3), borderRadius: '1px' }} />,
                                                                strong: ({ children }) => <strong style={{ fontWeight: 700, color: theme.palette.text.primary }}>{children}</strong>,
                                                                em: ({ children }) => <em style={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</em>,
                                                                a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none', borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>{children}</a>,
                                                                code: ({ inline, children, ...props }) => inline ? <code style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8), padding: '2px 6px', borderRadius: '4px', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.85em', color: theme.palette.text.primary }} {...props}>{children}</code> : <pre style={{ backgroundColor: alpha(theme.palette.grey[900], 0.05), border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: '8px', padding: '16px', margin: '16px 0', overflow: 'auto', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.9em', lineHeight: '1.6', color: theme.palette.text.primary, whiteSpace: 'pre', tabSize: 4 }}><code {...props}>{children}</code></pre>
                                                            }}>
                                                                {newQcTest.expected_value || '*Type to see live preview...*'}
                                                            </ReactMarkdown>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Card>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            {/* Test Results Markdown Editor */}
                                            <Card sx={{ 
                                                borderRadius: 2, 
                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                minHeight: '300px'
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center', 
                                                    p: 2, 
                                                    pb: 0
                                                }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                        Test Results/Observations *
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => openTemplateDialog('result')}
                                                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                        >
                                                            Load Template
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => openSaveTemplateDialog('result')}
                                                            disabled={!newQcTest.actual_value.trim()}
                                                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                        >
                                                            Save as Template
                                                        </Button>
                                                        <IconButton
                                                            onClick={() => setActualPreviewMode(!actualPreviewMode)}
                                                            sx={{
                                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                                borderRadius: 2,
                                                                backgroundColor: 'white'
                                                            }}
                                                        >
                                                            {actualPreviewMode ? <EditIcon /> : <PreviewIcon />}
                                                        </IconButton>
                                                    </Box>
                                                </Box>

                                                {!actualPreviewMode && (
                                                    <MarkdownToolbar onInsert={(before, after) => {
                                                        setActiveField('actual');
                                                        insertMarkdown(before, after);
                                                    }} />
                                                )}

                                                <Box sx={{ 
                                                    position: 'relative', 
                                                    height: '800px',
                                                    display: 'flex',
                                                    overflow: 'hidden'
                                                }}>
                                                    <Box sx={{ 
                                                        width: actualPreviewMode ? '0%' : (newQcTest.actual_value ? '50%' : '100%'),
                                                        height: '100%',
                                                        transition: 'width 0.3s ease',
                                                        opacity: actualPreviewMode ? 0 : 1
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            value={newQcTest.actual_value}
                                                            onChange={(e) => handleInputChange('actual_value', e.target.value)}
                                                            onKeyDown={(e) => handleKeyDown(e, 'actual_value')}
                                                            onFocus={() => setActiveField('actual')}
                                                            placeholder="Record your findings and measurements..."
                                                            inputRef={setActualValueRef}
                                                            sx={{
                                                                height: '100%',
                                                                '& .MuiInputBase-root': { height: '100%', alignItems: 'stretch' },
                                                                '& .MuiInputBase-inputMultiline': { height: '100% !important', resize: 'none' },
                                                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                                                            }}
                                                            InputProps={{
                                                                sx: {
                                                                    fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                                                    fontSize: '0.9rem',
                                                                    padding: 2
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    
                                                    {(newQcTest.actual_value || actualPreviewMode) && (
                                                        <Box sx={{ 
                                                            width: actualPreviewMode ? '100%' : '50%',
                                                            height: '100%',
                                                            borderLeft: actualPreviewMode ? 'none' : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                            overflow: 'auto',
                                                            backgroundColor: alpha(theme.palette.grey[50], 0.3),
                                                            p: 2
                                                        }}>
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                                                h1: ({ children }) => <h1 style={{ fontSize: '1.5rem', fontWeight: 500, margin: '24px 0 16px 0', color: theme.palette.text.primary, borderBottom: `2px solid ${theme.palette.primary.main}`, paddingBottom: '8px' }}>{children}</h1>,
                                                                h2: ({ children }) => <h2 style={{ fontSize: '1.25rem', fontWeight: 500, margin: '20px 0 12px 0', color: theme.palette.text.primary, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, paddingBottom: '6px' }}>{children}</h2>,
                                                                h3: ({ children }) => <h3 style={{ fontSize: '1.1rem', fontWeight: 500, margin: '16px 0 8px 0', color: theme.palette.text.primary }}>{children}</h3>,
                                                                p: ({ children }) => <p style={{ margin: '8px 0', lineHeight: '1.6', color: theme.palette.text.primary }}>{children}</p>,
                                                                ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ul>,
                                                                ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ol>,
                                                                li: ({ children }) => (
                                                                        <li style={{
                                                                            margin: '4px 0',
                                                                            lineHeight: '1.5',
                                                                            color: theme.palette.text.primary
                                                                        }}>
                                                                            {children}
                                                                        </li>
                                                                ),
                                                                blockquote: ({ children }) => <blockquote style={{ margin: '16px 0', padding: '12px 16px', borderLeft: `4px solid ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: '0 4px 4px 0', fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</blockquote>,
                                                                table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', margin: '16px 0', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>{children}</table>,
                                                                thead: ({ children }) => <thead style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8) }}>{children}</thead>,
                                                                th: ({ children }) => <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, backgroundColor: alpha(theme.palette.grey[100], 0.8), color: theme.palette.text.primary }}>{children}</th>,
                                                                td: ({ children }) => <td style={{ padding: '12px', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, color: theme.palette.text.primary }}>{children}</td>,
                                                                hr: () => <hr style={{ margin: '24px 0', border: 'none', height: '2px', backgroundColor: alpha(theme.palette.grey[400], 0.3), borderRadius: '1px' }} />,
                                                                strong: ({ children }) => <strong style={{ fontWeight: 700, color: theme.palette.text.primary }}>{children}</strong>,
                                                                em: ({ children }) => <em style={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</em>,
                                                                a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none', borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>{children}</a>,
                                                                code: ({ inline, children, ...props }) => inline ? <code style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8), padding: '2px 6px', borderRadius: '4px', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.85em', color: theme.palette.text.primary }} {...props}>{children}</code> : <pre style={{ backgroundColor: alpha(theme.palette.grey[900], 0.05), border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: '8px', padding: '16px', margin: '16px 0', overflow: 'auto', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.9em', lineHeight: '1.6', color: theme.palette.text.primary, whiteSpace: 'pre', tabSize: 4 }}><code {...props}>{children}</code></pre>
                                                            }}>
                                                                {newQcTest.actual_value || '*Type to see live preview...*'}
                                                            </ReactMarkdown>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Card>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Pass/Fail Status</InputLabel>
                                                <Select
                                                    value={newQcTest.pass_fail_status}
                                                    onChange={(e) => setNewQcTest({...newQcTest, pass_fail_status: e.target.value})}
                                                    label="Pass/Fail Status"
                                                >
                                                    <MenuItem value="PASS">PASS</MenuItem>
                                                    <MenuItem value="FAIL">FAIL</MenuItem>
                                                    <MenuItem value="PENDING">PENDING</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Tested By *"
                                                value={newQcTest.tested_by}
                                                onChange={(e) => setNewQcTest({...newQcTest, tested_by: e.target.value})}
                                                placeholder="Inspector name"
                                                required
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={4}>
                                            <DateTimePicker
                                                label="Test Date/Time"
                                                value={newQcTest.tested_at}
                                                onChange={(newValue) => setNewQcTest({...newQcTest, tested_at: newValue})}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                ampm={false}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(1)}
                                        sx={{ mr: 1 }}
                                    >
                                        Continue
                                    </Button>
                                    <Button onClick={handleBack}>
                                        Back
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Step 3: Equipment */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Equipment
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Record equipment used during testing
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Box sx={{ mt: 2, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <EquipmentIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                Testing Equipment
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<SearchIcon />}
                                                onClick={() => openEquipmentDialog()}
                                                size="small"
                                                sx={{
                                                    borderRadius: 2,
                                                    px: 2,
                                                    py: 1
                                                }}
                                            >
                                                Search Equipment
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={addEquipment}
                                                size="small"
                                                sx={{
                                                    borderRadius: 2,
                                                    px: 2,
                                                    py: 1
                                                }}
                                            >
                                                Add Manually
                                            </Button>
                                        </Box>
                                    </Box>

                                    {equipmentList.length === 0 ? (
                                        <Card sx={{ 
                                            p: 4, 
                                            textAlign: 'center', 
                                            backgroundColor: alpha(theme.palette.grey[50], 0.8),
                                            border: `1px dashed ${theme.palette.grey[400]}`
                                        }}>
                                            <EquipmentIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                                No Equipment Added
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Add equipment used during this quality control test
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<SearchIcon />}
                                                    onClick={() => openEquipmentDialog()}
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    Search Equipment
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    onClick={addEquipment}
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    Add Manually
                                                </Button>
                                            </Box>
                                        </Card>
                                    ) : (
                                        <Grid container spacing={3}>
                                            {equipmentList.map((equipment, index) => (
                                                <Grid item xs={12} key={equipment.id}>
                                                    <Card sx={{ 
                                                        p: 3, 
                                                        borderRadius: 2, 
                                                        border: `1px solid ${theme.palette.grey[300]}`,
                                                        position: 'relative'
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                                Equipment #{index + 1}
                                                            </Typography>
                                                            <IconButton
                                                                onClick={() => removeEquipment(equipment.id)}
                                                                size="small"
                                                                sx={{
                                                                    color: 'error.main',
                                                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                                                    borderRadius: 2,
                                                                    '&:hover': {
                                                                        bgcolor: 'error.main',
                                                                        color: 'white'
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                        
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Equipment Name *"
                                                                    value={equipment.equipment_name}
                                                                    onChange={(e) => updateEquipment(equipment.id, 'equipment_name', e.target.value)}
                                                                    placeholder="e.g., Digital Scale, pH Meter, Microscope"
                                                                    required
                                                                    InputProps={{
                                                                        endAdornment: (
                                                                            <InputAdornment position="end">
                                                                                <Tooltip title="Search from equipment list">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => openEquipmentDialog(index)}
                                                                                    >
                                                                                        <SearchIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </InputAdornment>
                                                                        )
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Equipment Number/ID *"
                                                                    value={equipment.equipment_number}
                                                                    onChange={(e) => updateEquipment(equipment.id, 'equipment_number', e.target.value)}
                                                                    placeholder="e.g., SCL-001, PH-M-005, MIC-2023-15"
                                                                    required
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <TextField
                                                                    fullWidth
                                                                    multiline
                                                                    rows={5}
                                                                    label="How Equipment Was Used *"
                                                                    value={equipment.usage_description}
                                                                    onChange={(e) => updateEquipment(equipment.id, 'usage_description', e.target.value)}
                                                                    placeholder="Describe in detail how this equipment was used in the test, including settings, procedures, calibration status, etc..."
                                                                    required
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ mr: 1 }}
                                    >
                                        Continue
                                    </Button>
                                    <Button onClick={handleBack}>
                                        Back
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Step 4: Confirmation */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Confirmation
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Review and finalize the quality control test
                                </Typography>
                            </StepLabel>
                            <StepContent>
                                <Box sx={{ mt: 2, mb: 3 }}>
                                    {/* Test Summary */}
                                    <Card sx={{ mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.grey[300]}` }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500}}>
                                                Test Summary
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Material Name</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{newQcTest.material_name || 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Category</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {React.createElement(qcCategories[newQcTest.category]?.icon, { 
                                                            sx: { fontSize: 18, mr: 1, color: theme.palette.primary.main } 
                                                        })}
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {qcCategories[newQcTest.category]?.name}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Test Name</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                        {isCustomTest ? customTestName : newQcTest.test_name}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Status</Typography>
                                                    <Chip
                                                        icon={getStatusIcon(newQcTest.pass_fail_status)}
                                                        label={newQcTest.pass_fail_status}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: alpha(getStatusColor(newQcTest.pass_fail_status), 0.1),
                                                            color: getStatusColor(newQcTest.pass_fail_status),
                                                            border: `1px solid ${getStatusColor(newQcTest.pass_fail_status)}`,
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>

                                    {/* Deviation Notes for Failed Tests */}
                                    {newQcTest.pass_fail_status === 'FAIL' && (
                                        <Box sx={{ mb: 3 }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Deviation Notes *"
                                                value={newQcTest.deviation_notes}
                                                onChange={(e) => setNewQcTest({...newQcTest, deviation_notes: e.target.value})}
                                                placeholder="Required for failed tests: Describe the deviation, root cause analysis, and corrective actions..."
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: alpha(theme.palette.error.main, 0.05),
                                                        '& fieldset': {
                                                            borderColor: theme.palette.error.main,
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleAddQcTest}
                                        disabled={!validateStep(activeStep) || saving}
                                        sx={{ 
                                            mr: 1,
                                            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                                            }
                                        }}
                                    >
                                        {saving ? 'Saving...' : (editMode ? 'Update Quality Test' : 'Add Quality Test')}
                                    </Button>
                                    <Button onClick={handleBack}>
                                        Back
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>
                    </Stepper>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Button 
                        onClick={() => {
                            setShowAddDialog(false);
                            handleReset();
                        }}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            color: theme.palette.grey[600]
                        }}
                    >
                        Cancel
                    </Button>
                    {activeStep > 0 && (
                        <Button 
                            onClick={handleReset}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                color: theme.palette.grey[600]
                            }}
                        >
                            Reset
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* View QC Test Dialog */}
            <Dialog 
                open={showViewDialog} 
                onClose={() => {
                    setShowViewDialog(false);
                    setSelectedTest(null);
                }} 
                maxWidth={false}
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        minHeight: '600px',
                        width: '95vw',
                        maxWidth: '2000px'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.05), 
                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                    fontWeight: 500
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Supply Quality Control Test Details
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 4, mt: 3 }}>
                    {selectedTest && (
                        <Box>
                            {/* Material Name Header */}
                            <Card sx={{ mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                <CardContent sx={{ py: 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 400, color: theme.palette.primary.main, textAlign: 'center' }}>
                                        {selectedTest.material_name || 'Unknown Material'}
                                    </Typography>
                                    <Typography variant="subtitle1" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                                        {qcCategories[selectedTest.category]?.name} Quality Control Test
                                    </Typography>
                                    {selectedTest.material_supplier && (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                                            Supplier: {selectedTest.material_supplier}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                            
                            {/* Test Summary */}
                            <Card sx={{ mb: 4, borderRadius: 2, border: `1px solid ${theme.palette.grey[300]}` }}>
                                <CardContent>
                                    <Typography variant="h3" sx={{ mb: 3, fontWeight: 500 }}>
                                        Test Summary
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Category</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {React.createElement(qcCategories[selectedTest.category]?.icon, { 
                                                    sx: { fontSize: 18, mr: 1, color: theme.palette.primary.main } 
                                                })}
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {qcCategories[selectedTest.category]?.name}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Test Name</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {selectedTest.test_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 3 }}>
                                            <Typography variant="h4" color="text.primary" sx={{ mb: 3, fontWeight: 500 }}>Specifications</Typography>
                                            <Box sx={{ 
                                                height: '2000px',
                                                overflow: 'auto',
                                                backgroundColor: alpha(theme.palette.grey[50], 0.3),
                                                p: 2,
                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                borderRadius: 2
                                            }}>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                                    h1: ({ children }) => <h1 style={{ fontSize: '1.5rem', fontWeight: 500, margin: '24px 0 16px 0', color: theme.palette.text.primary, borderBottom: `2px solid ${theme.palette.primary.main}`, paddingBottom: '8px' }}>{children}</h1>,
                                                    h2: ({ children }) => <h2 style={{ fontSize: '1.25rem', fontWeight: 500, margin: '20px 0 12px 0', color: theme.palette.text.primary, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, paddingBottom: '6px' }}>{children}</h2>,
                                                    h3: ({ children }) => <h3 style={{ fontSize: '1.1rem', fontWeight: 500, margin: '16px 0 8px 0', color: theme.palette.text.primary }}>{children}</h3>,
                                                    p: ({ children }) => <p style={{ margin: '8px 0', lineHeight: '1.6', color: theme.palette.text.primary }}>{children}</p>,
                                                    ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ul>,
                                                    ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ol>,
                                                    li: ({ children }) => <li style={{ margin: '4px 0', lineHeight: '1.5', color: theme.palette.text.primary }}>{children}</li>,
                                                    blockquote: ({ children }) => <blockquote style={{ margin: '16px 0', padding: '12px 16px', borderLeft: `4px solid ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: '0 4px 4px 0', fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</blockquote>,
                                                    table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', margin: '16px 0', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>{children}</table>,
                                                    thead: ({ children }) => <thead style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8) }}>{children}</thead>,
                                                    th: ({ children }) => <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, backgroundColor: alpha(theme.palette.grey[100], 0.8), color: theme.palette.text.primary }}>{children}</th>,
                                                    td: ({ children }) => <td style={{ padding: '12px', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, color: theme.palette.text.primary }}>{children}</td>,
                                                    hr: () => <hr style={{ margin: '24px 0', border: 'none', height: '2px', backgroundColor: alpha(theme.palette.grey[400], 0.3), borderRadius: '1px' }} />,
                                                    strong: ({ children }) => <strong style={{ fontWeight: 700, color: theme.palette.text.primary }}>{children}</strong>,
                                                    em: ({ children }) => <em style={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</em>,
                                                    code: ({ inline, children, ...props }) => inline ? <code style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8), padding: '2px 6px', borderRadius: '4px', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.85em', color: theme.palette.text.primary }} {...props}>{children}</code> : <pre style={{ backgroundColor: alpha(theme.palette.grey[900], 0.05), border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: '8px', padding: '16px', margin: '16px 0', overflow: 'auto', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.9em', lineHeight: '1.6', color: theme.palette.text.primary, whiteSpace: 'pre', tabSize: 4 }}><code {...props}>{children}</code></pre>
                                                }}>
                                                    {selectedTest.expected_value || '*No specifications provided*'}
                                                </ReactMarkdown>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 4 }}>
                                            <Typography variant="h4" color="text.primary" sx={{ mb: 3, fontWeight: 500 }}>Results</Typography>
                                            <Box sx={{ 
                                                height: '2000px',
                                                overflow: 'auto',
                                                backgroundColor: alpha(theme.palette.grey[50], 0.3),
                                                p: 2,
                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                                borderRadius: 2
                                            }}>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                                    h1: ({ children }) => <h1 style={{ fontSize: '1.5rem', fontWeight: 500, margin: '24px 0 16px 0', color: theme.palette.text.primary, borderBottom: `2px solid ${theme.palette.primary.main}`, paddingBottom: '8px' }}>{children}</h1>,
                                                    h2: ({ children }) => <h2 style={{ fontSize: '1.25rem', fontWeight: 500, margin: '20px 0 12px 0', color: theme.palette.text.primary, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, paddingBottom: '6px' }}>{children}</h2>,
                                                    h3: ({ children }) => <h3 style={{ fontSize: '1.1rem', fontWeight: 500, margin: '16px 0 8px 0', color: theme.palette.text.primary }}>{children}</h3>,
                                                    p: ({ children }) => <p style={{ margin: '8px 0', lineHeight: '1.6', color: theme.palette.text.primary }}>{children}</p>,
                                                    ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ul>,
                                                    ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px', color: theme.palette.text.primary }}>{children}</ol>,
                                                    li: ({ children }) => <li style={{ margin: '4px 0', lineHeight: '1.5', color: theme.palette.text.primary }}>{children}</li>,
                                                    blockquote: ({ children }) => <blockquote style={{ margin: '16px 0', padding: '12px 16px', borderLeft: `4px solid ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.grey[100], 0.5), borderRadius: '0 4px 4px 0', fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</blockquote>,
                                                    table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', margin: '16px 0', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>{children}</table>,
                                                    thead: ({ children }) => <thead style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8) }}>{children}</thead>,
                                                    th: ({ children }) => <th style={{ padding: '12px', textAlign: 'left', fontWeight: 500, border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, backgroundColor: alpha(theme.palette.grey[100], 0.8), color: theme.palette.text.primary }}>{children}</th>,
                                                    td: ({ children }) => <td style={{ padding: '12px', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, color: theme.palette.text.primary }}>{children}</td>,
                                                    hr: () => <hr style={{ margin: '24px 0', border: 'none', height: '2px', backgroundColor: alpha(theme.palette.grey[400], 0.3), borderRadius: '1px' }} />,
                                                    strong: ({ children }) => <strong style={{ fontWeight: 700, color: theme.palette.text.primary }}>{children}</strong>,
                                                    em: ({ children }) => <em style={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>{children}</em>,
                                                    code: ({ inline, children, ...props }) => inline ? <code style={{ backgroundColor: alpha(theme.palette.grey[100], 0.8), padding: '2px 6px', borderRadius: '4px', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.85em', color: theme.palette.text.primary }} {...props}>{children}</code> : <pre style={{ backgroundColor: alpha(theme.palette.grey[900], 0.05), border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: '8px', padding: '16px', margin: '16px 0', overflow: 'auto', fontFamily: 'Monaco, Consolas, "Lucida Console", monospace', fontSize: '0.9em', lineHeight: '1.6', color: theme.palette.text.primary, whiteSpace: 'pre', tabSize: 4 }}><code {...props}>{children}</code></pre>
                                                }}>
                                                    {selectedTest.actual_value || '*No results provided*'}
                                                </ReactMarkdown>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 4 }}>
                                            <Typography variant="h4" color="text.primary" sx={{ mb: 2, fontWeight: 500 }}>Status</Typography>
                                            <Chip
                                                icon={getStatusIcon(selectedTest.pass_fail_status)}
                                                label={selectedTest.pass_fail_status}
                                                size="medium"
                                                sx={{
                                                    backgroundColor: alpha(getStatusColor(selectedTest.pass_fail_status), 0.1),
                                                    color: getStatusColor(selectedTest.pass_fail_status),
                                                    border: `1px solid ${getStatusColor(selectedTest.pass_fail_status)}`,
                                                    fontWeight: 500,
                                                    fontSize: '1rem',
                                                    px: 2,
                                                    py: 1
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 3 }}>
                                            <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Tested By</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>{selectedTest.tested_by}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 3 }}>
                                            <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Test Date</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                                                {dayjs(selectedTest.tested_at).format('MMMM DD, YYYY HH:mm')}
                                            </Typography>
                                        </Grid>
                                        {selectedTest.batch_number && (
                                            <Grid item xs={12} md={6} sx={{ mt: 3 }}>
                                                <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Batch Number</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedTest.batch_number}</Typography>
                                            </Grid>
                                        )}
                                        {selectedTest.lot_number && (
                                            <Grid item xs={12} md={6} sx={{ mt: 3 }}>
                                                <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Lot Number</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedTest.lot_number}</Typography>
                                            </Grid>
                                        )}
                                        {selectedTest.sample_size && (
                                            <Grid item xs={12} md={6} sx={{ mt: 3 }}>
                                                <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Sample Size</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedTest.sample_size}</Typography>
                                            </Grid>
                                        )}
                                        {selectedTest.sample_id && (
                                            <Grid item xs={12} md={6} sx={{ mt: 3 }}>
                                                <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Sample ID</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedTest.sample_id}</Typography>
                                            </Grid>
                                        )}
                                        {selectedTest.notes && (
                                            <Grid item xs={12} sx={{ mt: 3 }}>
                                                <Typography variant="h5" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>Additional Notes</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedTest.notes}</Typography>
                                            </Grid>
                                        )}
                                        {selectedTest.deviation_notes && (
                                            <Grid item xs={12} sx={{ mt: 3 }}>
                                                <Typography variant="h5" color="error.main" sx={{ mb: 1, fontWeight: 500 }}>Deviation Notes</Typography>
                                                <Box sx={{ 
                                                    p: 2, 
                                                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                                                    border: `1px solid ${theme.palette.error.main}`,
                                                    borderRadius: 2
                                                }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedTest.deviation_notes}</Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                            
                            {/* Equipment Section */}
                            <Card sx={{ mb: 4, borderRadius: 2, border: `1px solid ${theme.palette.grey[300]}` }}>
                                <CardContent>
                                    <Typography variant="h3" sx={{ mb: 3, fontWeight: 500 }}>
                                        Equipment Used
                                    </Typography>
                                    <EquipmentDisplay testId={selectedTest.id} />
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Button 
                        onClick={() => {
                            setShowViewDialog(false);
                            setSelectedTest(null);
                        }}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={showDeleteDialog} 
                onClose={() => {
                    setShowDeleteDialog(false);
                    setTestToDelete(null);
                }}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.error.main, 0.05), 
                    borderBottom: `1px solid ${theme.palette.error.main}`,
                    fontWeight: 500
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WarningIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                            Delete Supply Quality Control Test
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {testToDelete && (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', mt: 2 }}>
                                Are you sure you want to delete this supply quality control test? This action cannot be undone.
                            </Typography>
                            
                            <Card sx={{ 
                                border: `1px solid ${theme.palette.error.main}`,
                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                            }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                                        {testToDelete.test_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Category: {qcCategories[testToDelete.category]?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Tested by: {testToDelete.tested_by}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={() => {
                            setShowDeleteDialog(false);
                            setTestToDelete(null);
                        }}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDeleteQcTest}
                        variant="contained"
                        disabled={deleting}
                        sx={{
                            backgroundColor: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: theme.palette.error.dark
                            }
                        }}
                    >
                        {deleting ? 'Deleting...' : 'Delete Test'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* QA Review Dialog */}
            <Dialog 
                open={showReviewDialog} 
                onClose={() => {
                    setShowReviewDialog(false);
                    setTestUnderReview(null);
                }} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        minHeight: '500px'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.warning.main, 0.05), 
                    borderBottom: `1px solid ${theme.palette.warning.main}`,
                    fontWeight: 500
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon sx={{ color: theme.palette.warning.main, mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                            QA Review & Approval
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {testUnderReview && (
                        <Box>
                            {/* Test Summary */}
                            <Card sx={{ mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.grey[300]}`, mt: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                        Test Under Review
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        {testUnderReview.test_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Category: {qcCategories[testUnderReview.category]?.name} • 
                                        Tested by: {testUnderReview.tested_by} • 
                                        Status: {testUnderReview.pass_fail_status}
                                    </Typography>
                                </CardContent>
                            </Card>

                            {/* Review Form */}
                            <Card sx={{ mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                                        QA Review Information
                                    </Typography>
                                    
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Reviewer Name *"
                                                value={reviewerName}
                                                onChange={(e) => {
                                                    setReviewerName(e.target.value);
                                                    setSignaturePreview(e.target.value);
                                                }}
                                                placeholder="Enter your full name"
                                                required
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Reviewer Role</InputLabel>
                                                <Select
                                                    value={reviewerRole}
                                                    onChange={(e) => setReviewerRole(e.target.value)}
                                                    label="Reviewer Role"
                                                >
                                                    <MenuItem value="qa_manager">QA Manager</MenuItem>
                                                    <MenuItem value="qa_supervisor">QA Supervisor</MenuItem>
                                                    <MenuItem value="qa_director">QA Director</MenuItem>
                                                    <MenuItem value="production_manager">Production Manager</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Review Comments"
                                                value={reviewComments}
                                                onChange={(e) => setReviewComments(e.target.value)}
                                                placeholder="Optional: Add any review comments or observations..."
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ 
                                                mt: 2, 
                                                p: 2.5, 
                                                border: `1px solid ${theme.palette.grey[400]}`, 
                                                borderRadius: 2, 
                                                backgroundColor: alpha(theme.palette.grey[50], 0.8)
                                            }}>
                                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                                                    Signature Preview: Your signature will appear in script font
                                                </Typography>
                                                <Box sx={{ 
                                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                    fontSize: '28px', 
                                                    color: theme.palette.primary.main,
                                                    mt: 2,
                                                    mb: 1,
                                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                                    minHeight: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}>
                                                    {signaturePreview || 'Preview will appear here'}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Current Status */}
                            <Alert 
                                severity={testUnderReview.qa_review_status === 'APPROVED' ? 'success' : 'info'}
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Current Review Status: {testUnderReview.qa_review_status || 'PENDING'}
                                    {testUnderReview.qa_reviewed_by_name && (
                                        <> • Reviewed by: {testUnderReview.qa_reviewed_by_name}</>
                                    )}
                                </Typography>
                            </Alert>

                            {/* Show signature if approved */}
                            {testUnderReview.qa_review_status === 'APPROVED' && testUnderReview.qa_reviewed_by_name && (
                                <Card sx={{ 
                                    mb: 3,
                                    borderRadius: 2, 
                                    border: `1px solid ${theme.palette.success.main}`,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`
                                }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ 
                                                p: 1, 
                                                borderRadius: 2, 
                                                background: alpha(theme.palette.success.main, 0.2),
                                                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                            }}>
                                                <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                                            </Box>
                                            
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                                                    QA Approved
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    This test has been reviewed and approved
                                                </Typography>
                                                {canApprove && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                            setShowReviewDialog(false);
                                                            handleUnapproveTest(testUnderReview);
                                                        }}
                                                        sx={{
                                                            mt: 1,
                                                            borderColor: theme.palette.error.main,
                                                            color: theme.palette.error.main,
                                                            fontWeight: 500,
                                                            '&:hover': {
                                                                borderColor: theme.palette.error.dark,
                                                                backgroundColor: alpha(theme.palette.error.main, 0.04)
                                                            }
                                                        }}
                                                    >
                                                        Unapprove Test
                                                    </Button>
                                                )}
                                            </Box>

                                            <Box sx={{ 
                                                border: `1px solid ${theme.palette.success.main}`, 
                                                p: 2, 
                                                borderRadius: 2, 
                                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                                minWidth: 200
                                            }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                                    APPROVED BY:
                                                </Typography>
                                                <Box sx={{ 
                                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                    fontSize: '20px', 
                                                    color: theme.palette.success.main,
                                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                                    mb: 1
                                                }}>
                                                    {testUnderReview.qa_reviewed_by_name}
                                                </Box>
                                                {testUnderReview.qa_review_datetime && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                        {dayjs(testUnderReview.qa_review_datetime).format('MM/DD/YYYY HH:mm')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Button 
                        onClick={() => {
                            setShowReviewDialog(false);
                            setTestUnderReview(null);
                        }}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            color: theme.palette.grey[600]
                        }}
                    >
                        {testUnderReview?.qa_review_status === 'APPROVED' ? 'Close' : 'Cancel'}
                    </Button>
                    {testUnderReview?.qa_review_status !== 'APPROVED' && canApprove && (
                        <Button 
                            onClick={handleQAReview}
                            variant="contained"
                            disabled={!reviewerName.trim() || reviewing}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                backgroundColor: theme.palette.success.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.success.dark
                                }
                            }}
                        >
                            {reviewing ? 'Reviewing...' : 'Approve & Sign'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Unapprove QA Test Dialog */}
            <Dialog 
                open={showUnapproveDialog} 
                onClose={() => {
                    setShowUnapproveDialog(false);
                    setTestToUnapprove(null);
                }} 
                maxWidth="sm" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.error.main, 0.05), 
                    borderBottom: `1px solid ${theme.palette.error.main}`,
                    fontWeight: 500
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WarningIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                            Unapprove QA Test
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {testToUnapprove && (
                        <Box>
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                                    Are you sure you want to unapprove this QA test?
                                </Typography>
                                <Typography variant="body2">
                                    This will mark the test as pending review again and allow it to be edited. This action will be logged.
                                </Typography>
                            </Alert>

                            <Card sx={{ 
                                mb: 3,
                                border: `1px solid ${theme.palette.error.main}`,
                                backgroundColor: alpha(theme.palette.error.main, 0.05)
                            }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                                        {testToUnapprove.test_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Category: {qcCategories[testToUnapprove.category]?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Currently approved by: {testToUnapprove.qa_reviewed_by_name}
                                    </Typography>
                                </CardContent>
                            </Card>

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Reason for Unapproval *"
                                value={unapproveReason}
                                onChange={(e) => setUnapproveReason(e.target.value)}
                                placeholder="Required: Explain why this test needs to be unapproved..."
                                required
                                sx={{ mb: 3 }}
                            />

                            <TextField
                                fullWidth
                                label="Your Name (Signature) *"
                                value={unapproveSignature}
                                onChange={(e) => {
                                    setUnapproveSignature(e.target.value);
                                    setUnapproveSignaturePreview(e.target.value);
                                }}
                                placeholder="Enter your full name"
                                required
                                sx={{ mb: 2 }}
                            />

                            <Box sx={{ 
                                p: 2.5, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 2, 
                                backgroundColor: alpha(theme.palette.grey[50], 0.8)
                            }}>
                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                                    Signature Preview: Your signature will appear in script font
                                </Typography>
                                <Box sx={{ 
                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                    fontSize: '24px', 
                                    color: theme.palette.error.main,
                                    mt: 2,
                                    mb: 1,
                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                    minHeight: '35px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {unapproveSignaturePreview || 'Preview will appear here'}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Button 
                        onClick={() => {
                            setShowUnapproveDialog(false);
                            setTestToUnapprove(null);
                        }}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            color: theme.palette.grey[600]
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUnapproveQA}
                        variant="contained"
                        disabled={!unapproveSignature.trim() || !unapproveReason.trim() || unapproving}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            backgroundColor: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: theme.palette.error.dark
                            }
                        }}
                    >
                        {unapproving ? 'Unapproving...' : 'Confirm Unapproval'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Equipment Selection Dialog */}
            <Dialog
                open={showEquipmentDialog}
                onClose={() => {
                    setShowEquipmentDialog(false);
                    setEquipmentSearchTerm('');
                    setSelectedEquipmentFromList(null);
                    setCurrentEquipmentIndex(null);
                }}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        height: '40vh',
                        width: '600px',
                        maxWidth: '600px',
                        margin: 'auto',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    pb: 2, 
                    fontSize: '1.25rem', 
                    fontWeight: 500,
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                        Select Equipment
                    </Typography>
                    <IconButton onClick={() => {
                        setShowEquipmentDialog(false);
                        setEquipmentSearchTerm('');
                        setSelectedEquipmentFromList(null);
                        setCurrentEquipmentIndex(null);
                    }} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search equipment..."
                            value={equipmentSearchTerm}
                            onChange={(e) => setEquipmentSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.primary.main }} />
                                    </InputAdornment>
                                ),
                                sx: { 
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                }
                            }}
                            size="medium"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: 'none'
                                    },
                                    '&:hover fieldset': {
                                        border: 'none'
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none'
                                    }
                                }
                            }}
                        />
                    </Box>
                    
                    <Box sx={{ 
                        flex: 1,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}>
                        {loadingEquipment ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <Typography>Loading equipment...</Typography>
                            </Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Equipment</TableCell>
                                        <TableCell sx={{ fontWeight: 500, py: 2 }}>Number</TableCell>
                                        <TableCell sx={{ fontWeight: 500, py: 2 }}>Department</TableCell>
                                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredEquipment.map((eq) => (
                                        <TableRow 
                                            key={eq.id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bgcolor: 'grey.50',
                                                    transform: 'translateX(4px)',
                                                    boxShadow: 'inset 4px 0 0 black'
                                                },
                                                '& .MuiTableCell-root': {
                                                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                                    py: 2,
                                                    pl: 3,
                                                    pr: 3
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                    {eq.equipment_name}
                                                </Typography>
                                                {eq.make && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Make: {eq.make}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                    {eq.equipment_number || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                    {eq.department_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleEquipmentSelect(eq)}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        py: 1,
                                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                        bgcolor: selectedEquipmentFromList?.id === eq.id ? 'black' : 'transparent',
                                                        color: selectedEquipmentFromList?.id === eq.id ? 'white' : 'inherit',
                                                        '&:hover': {
                                                            bgcolor: selectedEquipmentFromList?.id === eq.id ? 'black' : 'transparent',
                                                            border: `1px solid ${theme.palette.primary.main}`,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                                        }
                                                    }}
                                                >
                                                    Select
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredEquipment.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {equipmentSearchTerm ? 'No equipment found matching your search.' : 'No equipment available.'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    gap: 2, 
                    borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                    bgcolor: alpha(theme.palette.grey[50], 0.5),
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}>
                    <Button
                        onClick={() => {
                            setShowEquipmentDialog(false);
                            setEquipmentSearchTerm('');
                            setSelectedEquipmentFromList(null);
                            setCurrentEquipmentIndex(null);
                        }}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2, 
                            px: 3, 
                            py: 1, 
                            fontSize: '0.9rem', 
                            fontWeight: 500,
                            textTransform: 'none',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                                border: `1px solid ${theme.palette.grey[600]}`,
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmEquipmentSelection}
                        variant="contained"
                        disabled={!selectedEquipmentFromList}
                        sx={{ 
                            borderRadius: 2, 
                            px: 3, 
                            py: 1, 
                            fontSize: '0.9rem', 
                            fontWeight: 500,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            '&:disabled': {
                                background: alpha(theme.palette.grey[400], 0.5),
                                color: alpha(theme.palette.grey[600], 0.7)
                            }
                        }}
                    >
                        Confirm Selection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Command Menu */}
            {commandMenuOpen && (
                <Popper
                    open={commandMenuOpen}
                    anchorEl={commandMenuAnchor}
                    placement="bottom-start"
                    sx={{ zIndex: 9999 }}
                >
                    <ClickAwayListener onClickAway={() => setCommandMenuOpen(false)}>
                        <Card sx={{ 
                            mt: 1, 
                            maxWidth: 300,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
                        }}>
                            <Box sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                                {(() => {
                                    const currentRef = activeField === 'expected' ? expectedValueRef : actualValueRef;
                                    const currentValue = activeField === 'expected' ? newQcTest.expected_value : newQcTest.actual_value;
                                    const cursorPos = currentRef?.selectionStart || currentValue.length;
                                    const beforeCursor = currentValue.substring(0, cursorPos);
                                    const lines = beforeCursor.split('\n');
                                    const currentLine = lines[lines.length - 1];
                                    const searchTerm = currentLine.startsWith('/') ? currentLine.substring(1).toLowerCase() : '';
                                    let currentIndex = 0;
                                    
                                    return commandSections.map((section) => {
                                        const sectionCommands = section.commands.filter(cmd => {
                                            if (searchTerm === '') return true;
                                            
                                            const nameMatch = cmd.name.substring(1).toLowerCase().includes(searchTerm);
                                            const aliasMatch = cmd.alias.substring(1).toLowerCase().includes(searchTerm);
                                            const descMatch = cmd.description.toLowerCase().includes(searchTerm);
                                            
                                            return nameMatch || aliasMatch || descMatch;
                                        });
                                        
                                        if (sectionCommands.length === 0) return null;
                                        
                                        const sectionItems = [];
                                        
                                        // Section header
                                        sectionItems.push(
                                            <Box key={section.title} sx={{ 
                                                px: 2, 
                                                py: 1, 
                                                backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                                            }}>
                                                <Typography variant="caption" sx={{ 
                                                    fontWeight: 600, 
                                                    color: theme.palette.primary.main,
                                                    fontSize: '0.75rem',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {section.title}
                                                </Typography>
                                            </Box>
                                        );
                                        
                                        // Section commands
                                        sectionCommands.forEach((command) => {
                                            const isSelected = currentIndex === selectedCommandIndex;
                                            sectionItems.push(
                                                <ListItem
                                                    key={command.name}
                                                    button
                                                    selected={isSelected}
                                                    onClick={() => executeCommand(command)}
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        backgroundColor: isSelected 
                                                            ? alpha(theme.palette.primary.main, 0.1) 
                                                            : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 1,
                                                            backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                                            color: theme.palette.text.secondary
                                                        }}>
                                                            {command.icon}
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                                {command.description.split(' ')[0]} {command.description.split(' ')[1] || ''}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                                {command.description}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            );
                                            currentIndex++;
                                        });
                                        
                                        return sectionItems;
                                    }).filter(Boolean).flat();
                                })()}
                            </Box>
                        </Card>
                    </ClickAwayListener>
                </Popper>
            )}

            {/* Lot/Batch Selection Dialog */}
            <Dialog
                open={showLotBatchDialog}
                onClose={() => setShowLotBatchDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        Select Lot/Batch from {newQcTest.category.charAt(0).toUpperCase() + newQcTest.category.slice(1)} Inventory
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose from available inventory items with lot/batch numbers
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by lot number, batch number, item name, SKU, or location..."
                            value={lotBatchSearchTerm}
                            onChange={(e) => setLotBatchSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: 2 }}
                        />
                    </Box>

                    {loadingLotBatches ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <Typography>Loading available lot/batch items...</Typography>
                        </Box>
                    ) : filteredLotBatches.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                                {availableLotBatches.length === 0 
                                    ? `No inventory items found for ${newQcTest.category}`
                                    : 'No items match your search criteria'
                                }
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {(() => {
                                // Group items by product name and SKU
                                const groupedItems = filteredLotBatches.reduce((acc, item) => {
                                    const key = `${item.item_name}_${item.item_sku}`;
                                    if (!acc[key]) {
                                        acc[key] = {
                                            item_name: item.item_name,
                                            item_sku: item.item_sku,
                                            items: []
                                        };
                                    }
                                    acc[key].items.push(item);
                                    return acc;
                                }, {});

                                return Object.values(groupedItems).map((group) => (
                                    <Box key={`${group.item_name}_${group.item_sku}`} sx={{ mb: 3 }}>
                                        {/* Product Header */}
                                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500, mb: 1 }}>
                                            {group.item_name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            SKU: {group.item_sku || 'N/A'}
                                        </Typography>
                                        
                                        {/* Indented Batch/Lot Items */}
                                        <Box sx={{ ml: 2 }}>
                                            {group.items.map((item) => (
                                                <Box
                                                    key={item.id}
                                                    sx={{
                                                        p: 2,
                                                        mb: 1,
                                                        cursor: 'pointer',
                                                        borderRadius: 1,
                                                        border: selectedLotBatch?.id === item.id ? 
                                                            `2px solid ${theme.palette.primary.main}` : 
                                                            '1px solid',
                                                        borderColor: selectedLotBatch?.id === item.id ? 
                                                            theme.palette.primary.main : 
                                                            theme.palette.divider,
                                                        backgroundColor: selectedLotBatch?.id === item.id ?
                                                            alpha(theme.palette.primary.main, 0.1) :
                                                            'transparent',
                                                        '&:hover': {
                                                            backgroundColor: selectedLotBatch?.id === item.id ?
                                                                alpha(theme.palette.primary.main, 0.15) :
                                                                alpha(theme.palette.primary.main, 0.05)
                                                        }
                                                    }}
                                                    onClick={() => handleLotBatchSelect(item)}
                                                >
                                                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                                        {item.batch_number && item.lot_number ? 
                                                            `Batch ${item.batch_number}, Lot ${item.lot_number}` :
                                                            item.batch_number ? 
                                                                `Batch ${item.batch_number}` :
                                                                item.lot_number ?
                                                                    `Lot ${item.lot_number}` :
                                                                    'No Batch/Lot Number'
                                                        }
                                                    </Typography>
                                                    <Typography variant="body1" color="text.primary">
                                                        Qty: {item.current_quantity} | Location: {item.location_name}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                ));
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowLotBatchDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!selectedLotBatch}
                        onClick={() => {
                            if (selectedLotBatch) {
                                handleLotBatchSelect(selectedLotBatch);
                            }
                        }}
                    >
                        Select
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Load Template Dialog */}
            <Dialog
                open={showTemplateDialog}
                onClose={() => setShowTemplateDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Load {templateType === 'specification' ? 'Specification' : 'Result'} Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Select a template to load into the editor
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {loadingTemplates ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <Typography>Loading templates...</Typography>
                        </Box>
                    ) : templates.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                                No templates found. Save your first template!
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {templates.map((template) => (
                                <Card
                                    key={template.id}
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        cursor: 'pointer',
                                        border: selectedTemplate?.id === template.id
                                            ? `2px solid ${theme.palette.primary.main}`
                                            : `1px solid ${theme.palette.grey[300]}`,
                                        '&:hover': {
                                            border: `2px solid ${theme.palette.primary.main}`,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                        }
                                    }}
                                    onClick={() => setSelectedTemplate(template)}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                {template.template_name}
                                                {template.is_default && (
                                                    <Chip
                                                        label="Default"
                                                        size="small"
                                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Category: {template.template_category || 'General'}
                                            </Typography>
                                        </Box>
                                        {!template.is_default && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDeleteTemplateDialog(template);
                                                }}
                                                sx={{ color: 'error.main' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => selectedTemplate && handleLoadTemplate(selectedTemplate)}
                        disabled={!selectedTemplate}
                    >
                        Load Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save Template Dialog */}
            <Dialog
                open={showSaveTemplateDialog}
                onClose={() => setShowSaveTemplateDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Save as {templateType === 'specification' ? 'Specification' : 'Result'} Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Save current content as a reusable template
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Template Name"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        placeholder="e.g., Physical Test Specification"
                        sx={{ mt: 2 }}
                        autoFocus
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        This will save for {newQcTest.category} tests
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowSaveTemplateDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveTemplate}
                        disabled={!newTemplateName.trim()}
                    >
                        Save Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Template Confirmation Dialog */}
            <Dialog
                open={showDeleteTemplateDialog}
                onClose={() => setShowDeleteTemplateDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.error.main, 0.05), 
                    borderBottom: `1px solid ${theme.palette.error.main}` 
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WarningIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                            Delete Template
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {templateToDelete && (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', mt: 2 }}>
                                Are you sure you want to delete this template? This action cannot be undone.
                            </Typography>
                            <Card sx={{ 
                                border: `1px solid ${theme.palette.error.main}`, 
                                backgroundColor: alpha(theme.palette.error.main, 0.05),
                                p: 2
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                                    {templateToDelete.template_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Type: {templateToDelete.template_type === 'specification' ? 'Specification' : 'Result'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Category: {templateToDelete.template_category || 'General'}
                                </Typography>
                            </Card>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setShowDeleteTemplateDialog(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteTemplate} 
                        variant="contained" 
                        sx={{ backgroundColor: theme.palette.error.main }}
                    >
                        Delete Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            </Box>
        </LocalizationProvider>
    );
};

export default SupplyQCTests;